import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import weighingService from '@/services/weighing';
import { Pesée, Produit } from '@/types';
import { useWeighing, useCurrentWeighing } from '@/contexts';
import VehicleInfoSection from '@/components/VehicleInfoSection';
import OriginSection from '@/components/OriginSection';
import { useAutocomplete } from '@/hooks/useAutocomplete';

import ClientSupplierSection from '@/components/ClientSupplierSection';

import HeaderTabs from '@/components/HeaderTabs';
import { WeighingProcess } from '@/components/modals/WeighingProcessModal';

import SearchableSelect from '@/components/SearchableSelect';
import WeighingStepper, { WeighingStep } from '@/components/WeighingStepper';
import WeightCapture from '@/components/WeightCapture';
import WeighingCodeDisplay from '@/components/WeighingCodeDisplay';
import { WeighingTicketData } from '@/components/WeighingTicket';
import TicketActions from '@/components/tickets/TicketActions';
import QualityPage from '@/pages/QualityPage';
import useWeighingWorkflow from '@/hooks/useWeighingWorkflow';
import { useAuth } from '@/hooks/useAuth'; // Hook pour récupérer l'utilisateur connecté
import { useScaleAgent } from '@/hooks/useScaleAgent';
import { convertPeseeToTicketData } from '@/utils/ticketDataConverter';
import { getDisplayPeseeCode } from '@/utils/ticketSequences';
import { resolveWeighingProcess } from '@/utils/weighingProcess';
import {
  computePoidsNet,
  resolvePoidsSacs,
  resolveNbSacsAcceptes,
  resolveBrutTareForStep3,
} from '@/utils/weighingNetCalc';

import DestinationSection from '@/components/DestinationSection';
import TransferStickerSection from '@/components/TransferStickerSection';
import QualityAnalysisSection from '@/components/QualityAnalysisSection';
import CurrentWeighingInfo from '@/components/CurrentWeighingInfo';
import PendingWeighingsList from '@/components/PendingWeighingsList';

// Imports pour les permissions
import PermissionGuard from '@/components/permissions/PermissionGuard';
import PermissionButton from '@/components/permissions/PermissionButton';
import { PERMISSIONS } from '@/types/permissions';

const NouvellePeseePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { setCurrentWeighing, updateWeighing } = useWeighing();
  const currentWeighing = useCurrentWeighing();
  
  // Hooks pour l'autocomplétion
  const { saveValue: saveProvenance } = useAutocomplete('provenance');
  const { saveValue: saveDestination } = useAutocomplete('destination');
  
  const [products, setProducts] = useState<Produit[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [conditionnements, setConditionnements] = useState<any[]>([]);
  const [conditionnementsLoading, setConditionnementsLoading] = useState(false);
  const [currentWeight, setCurrentWeight] = useState<number | null>(null);
  const [, setPontBasculeStatus] = useState<'connected' | 'disconnected' | 'error' | 'checking'>('checking');
  const [selectedPontBasculeId, setSelectedPontBasculeId] = useState<string>(''); // Pont bascule par défaut
  const [loadedWeighingId, setLoadedWeighingId] = useState<string | null>(null); // ID de la pesée actuellement chargée
  const [showWeighingDetailsModal, setShowWeighingDetailsModal] = useState(false);
  const [showTareSummaryModal, setShowTareSummaryModal] = useState(false);
  const [tareSummaryData, setTareSummaryData] = useState<any>(null);
  
  // États pour le formulaire de destination
  const [destinationFormData, setDestinationFormData] = useState({
    destination: '',
    magasin: '',
    numeroLot: ''
  });
  
  // weighingDetails est maintenant remplacé par currentWeighing du contexte
  // const [weighingDetails, setWeighingDetails] = useState<Pesée | null>(null);
  
  // États pour l'analyse qualité
  const [qualityAnalysis, setQualityAnalysis] = useState<any>(null);

  // États pour les informations du site et tenant
  const [siteInfo, setSiteInfo] = useState<{nom: string, id: string} | null>(null);
  const [tenantInfo, setTenantInfo] = useState<{nom: string, id: string} | null>(null);
  const [pontBasculeInfo, setPontBasculeInfo] = useState<{nom: string, site: string, tenant: string} | null>(null);

  // État pour indiquer si on charge une pesée existante
  const [isLoadingExistingWeighing, setIsLoadingExistingWeighing] = useState(false);
  const [showWeighingLoadedNotification, setShowWeighingLoadedNotification] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string>('');
  
  // États pour le modal des pesées en attente
  const [showPendingWeighingsModal, setShowPendingWeighingsModal] = useState(false);
  const [pendingWeighingsCount, setPendingWeighingsCount] = useState(0);
  
  // Référence pour éviter les déclenchements multiples du useEffect de copie des valeurs
  const hasCopiedValuesRef = useRef<{step: number, typePesee: string}>({step: 0, typePesee: ''});

  // Fonction pour charger une pesée existante et déterminer l'étape
  const loadExistingWeighing = async (weighingId: string, forcedStep?: number) => {
    try {
      setIsLoadingExistingWeighing(true);
      setLoadingStep('Récupération des données de la pesée...');
      // Récupérer les détails de la pesée
      const response = await weighingService.getPesée(parseInt(weighingId));
      
      if (response.success && response.data) {
        let weighing = response.data as Pesée;

        const weightValue = (value: unknown) => {
          if (value === null || value === undefined || value === '') return 0;
          const parsed = parseFloat(String(value));
          return Number.isNaN(parsed) ? 0 : parsed;
        };

        // 1er poids parfois enregistré dans tare (parcours marqué inversé à tort).
        // On le replace dans brut avant de décider l'étape, pour ne pas écraser la tare ensuite.
        if (weightValue(weighing.poids_tare) > 0 && weightValue(weighing.poids_brut) <= 0 && weighing.id
            && weighing.processus !== 'inverted') {
          const hasQuality =
            Boolean(weighing.agent_analyse_qualite || weighing.analyse_qualite);
          try {
            await weighingService.updatePesée(Number(weighing.id), {
              poids_brut: weighing.poids_tare,
              poids_tare: null,
              poids_net: null,
              processus: 'standard',
              statut: hasQuality ? 'EN_ATTENTE_TARE' : 'BRUT_ENREGISTRE',
            } as any);
            const repaired = await weighingService.getPesée(Number(weighing.id));
            if (repaired.success && repaired.data) {
              weighing = repaired.data as Pesée;
            } else {
              weighing = {
                ...weighing,
                poids_brut: weightValue(weighing.poids_tare),
                poids_tare: undefined,
                processus: 'standard',
                statut: hasQuality ? 'EN_ATTENTE_TARE' : 'BRUT_ENREGISTRE',
              };
            }
          } catch {
            /* la migration / commande de réparation rattrapera le reste */
          }
        }

        // Déterminer l'étape appropriée
        let targetStep = 1;
        const processus = resolveWeighingProcess(weighing);
        const isInverted = processus === 'inverted';
        
        // Si un step est forcé via l'URL, l'utiliser
        if (forcedStep) {
          targetStep = forcedStep;
        } else {
          // Vérifier d'abord les poids pour déterminer l'état réel de la pesée
          const hasTare = weighing.poids_tare && parseFloat(weighing.poids_tare.toString()) > 0;
          const hasBrut = weighing.poids_brut && parseFloat(weighing.poids_brut.toString()) > 0;
          
          // Vérifier si l'analyse qualité est enregistrée
          const hasQualityAnalysis = weighing.agent_analyse_qualite || 
                                    weighing.analyse_qualite ||
                                    (weighing.nb_sacs_declares !== undefined && weighing.nb_sacs_declares > 0 && weighing.agent_analyse_qualite);
          
        
          // Vérifier si la pesée est vraiment complétée (statut COMPLETEE ou ACHEVEE ET les deux poids présents)
          if ((weighing.statut === 'COMPLETEE' || weighing.statut === 'ACHEVEE') && hasBrut && hasTare) {
            // Pesée complétée avec les deux poids → Étape 4 (Ticket final)
            targetStep = 4;
          } else if (isInverted) {
          // Mode inversé : étape 1 = tare, étape 2 = analyse qualité, étape 3 = brut, étape 4 = ticket
          if (hasTare && hasBrut) {
            // Les deux poids sont enregistrés → Étape 4 (Ticket final)
            targetStep = 4;
          } else if (hasTare && !hasBrut) {
            // Poids tare enregistré, vérifier si l'analyse qualité est réellement enregistrée
            if (hasQualityAnalysis) {
              // Analyse qualité enregistrée → Étape 3 (Pesée brut)
              targetStep = 3;
            } else {
              // Analyse qualité pas encore enregistrée → Étape 2 (Analyse qualité)
              targetStep = 2;
            }
          } else {
            // Aucun poids ou début → Étape 1 (Pesée tare)
            targetStep = 1;
          }
        } else {
          // Mode standard : étape 1 = brut, étape 2 = analyse qualité, étape 3 = tare, étape 4 = ticket
          if (hasBrut && hasTare) {
            // Les deux poids présents → Étape 4 (Ticket final)
            targetStep = 4;
          } else if (hasBrut && !hasTare) {
            // Poids brut enregistré mais pas de tare, vérifier si l'analyse qualité est faite
            if (hasQualityAnalysis) {
              // Analyse qualité enregistrée → Étape 3 (Pesée tare)
              targetStep = 3;
            } else {
              // Analyse qualité pas encore enregistrée → Étape 2 (Analyse qualité)
              targetStep = 2;
            }
          } else {
            // Aucun poids enregistré → Étape 1 (Pesée brut)
            targetStep = 1;
          }
          }
        }
        setLoadingStep('Chargement des données du formulaire...');

        // Utiliser la fonction de synchronisation complète
        syncWeighingDataToForm(weighing);

        // Définir la pesée comme actuelle
        setCurrentWeighing(weighing);
        setLoadedWeighingId(weighingId);
        
        // Réinitialiser qualityAnalysis pour éviter les données d'une autre pesée
        setQualityAnalysis(undefined);
        
        // Mettre à jour le code de pesée avec le reference du backend
        if (weighing.reference) {
          updateWeighingCode(getDisplayPeseeCode(weighing), weighing.id?.toString());
        }
        
        // Initialiser le processus depuis la pesée SANS remettre l'étape à 1
        initializeWeighingProcess(processus, false);

        // Aller à l'étape appropriée
        setLoadingStep('Redirection vers l\'étape appropriée...');
        goToStep(targetStep);

        // Récupérer l'analyse qualité si nécessaire
        if (targetStep >= 2) {
          setLoadingStep('Récupération de l\'analyse qualité...');
          const hasQualityAnalysis = await fetchQualityAnalysis(weighingId);
          
          // Si on est en mode inversé et qu'on a le poids tare mais pas le poids brut,
          // vérifier si l'analyse qualité existe réellement
          const isInvertedProcess = processus === 'inverted';
          
          if (isInvertedProcess && weighing.poids_tare && !weighing.poids_brut) {
            // Vérifier si l'analyse qualité a été réellement enregistrée
            const qualityAnalysisExists = hasQualityAnalysis || 
                                        weighing.agent_analyse_qualite || 
                                        weighing.analyse_qualite;
            
            if (!qualityAnalysisExists) {
              // Analyse qualité pas enregistrée, forcer l'étape 2
              targetStep = 2;
              goToStep(2);
            }
          }
        }

        // Afficher la notification de succès
        setShowWeighingLoadedNotification(true);
        setTimeout(() => setShowWeighingLoadedNotification(false), 5000);
      } else {
        alert('Erreur lors du chargement de la pesée. Veuillez réessayer.');
      }
    } catch (error) {
      alert('Erreur lors du chargement de la pesée. Veuillez réessayer.');
    } finally {
      setIsLoadingExistingWeighing(false);
      setLoadingStep('');
    }
  };

  const { user } = useAuth();
  
  // Hook pour le scale agent
  const { currentWeight: scaleWeight, isConnected } = useScaleAgent(user?.sites?.[0]?.id, selectedPontBasculeId);
  
  

  
  // Fonction pour récupérer le premier pont bascule disponible
  const fetchDefaultPontBascule = async () => {
    try {
      const { PontBasculeService } = await import('@/services/pontBasculeService');
      const pontBasculeService = PontBasculeService.getInstance();
      
      const pontsBascule = await pontBasculeService.getAllPontsBascule();
      
      if (pontsBascule && pontsBascule.length > 0) {
        const firstPont = pontsBascule[0];
        setSelectedPontBasculeId(firstPont.id);
        return firstPont.id;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Fonction pour récupérer les informations du pont bascule, site et tenant
  const fetchPontBasculeInfo = async (pontBasculeId: string) => {
    if (!pontBasculeId) {
      return;
    }
    
    try {
      // Importer le service des ponts bascule
      const { PontBasculeService } = await import('@/services/pontBasculeService');
      const pontBasculeService = PontBasculeService.getInstance();
      
      const pontBascule = await pontBasculeService.getPontBasculeById(pontBasculeId);
      
      if (pontBascule) {
        setPontBasculeInfo({
          nom: pontBascule.name || `Pont ${pontBasculeId}`,
          site: pontBascule.site_name || 'Site principal',
          tenant: pontBascule.tenant_name || 'Tenant principal'
        });
        
        // Mettre à jour les informations du site et tenant
        if (pontBascule.site_name) {
          setSiteInfo({
            nom: pontBascule.site_name,
            id: pontBascule.siteId || ''
          });
        }
        
        if (pontBascule.tenant_name) {
          setTenantInfo({
            nom: pontBascule.tenant_name,
            id: pontBascule.tenantId || ''
          });
        }
      } else {
        // Valeurs par défaut si la récupération échoue
        setPontBasculeInfo({
          nom: `Pont ${pontBasculeId}`,
          site: 'Site principal',
          tenant: 'Tenant principal'
        });
        setSiteInfo({ nom: 'Site principal', id: pontBasculeId });
        setTenantInfo({ nom: 'Tenant principal', id: '1' });
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des informations du pont bascule:', err);
      // Valeurs par défaut en cas d'erreur
      setPontBasculeInfo({
        nom: `Pont ${pontBasculeId}`,
        site: 'Site principal',
        tenant: 'Tenant principal'
      });
      setSiteInfo({ nom: 'Site principal', id: pontBasculeId });
      setTenantInfo({ nom: 'Tenant principal', id: '1' });
    }
  };
  
  // Fonction pour récupérer l'analyse qualité
  const fetchQualityAnalysis = async (weighingId: string): Promise<boolean> => {
    if (!weighingId) {
      return false;
    }
    
  
    
    try {
      // Importer le service d'analyse qualité
      const qualityAnalysisService = (await import('@/services/qualityAnalysis')).default;
      
      
      const response = await (qualityAnalysisService as any).getQualityAnalysisByPeseeId(parseInt(weighingId));
      
     
      if (response.success && response.data) {
        // Vérifier que l'analyse qualité correspond bien à la pesée demandée
        const analysisPeseeId = response.data.pesee;
        const requestedWeighingId = parseInt(weighingId);
        
        if (analysisPeseeId && analysisPeseeId !== requestedWeighingId) {
          setQualityAnalysis(null);
          return false;
        }
        
        setQualityAnalysis(response.data);
        return true; // Analyse qualité trouvée
      } else {
        setQualityAnalysis(null);
        return false; // Aucune analyse qualité
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de l\'analyse qualité:', err);
      setQualityAnalysis(null);
      return false; // Erreur, considérer comme non trouvée
    }
  };
  
  // Fonction pour récupérer les détails complets de la pesée
  const fetchWeighingDetails = async (weighingId: string) => {
    if (!weighingId) {
      return;
    }
    
    try {
      
      const response = await weighingService.getPesée(parseInt(weighingId));
      
      
      // Gérer différents formats de réponse
      let weighingData = null;
      
      if (response.success && response.data) {
        weighingData = response.data;
      } else if (response.data && !response.success) {
        weighingData = response.data;
      } else if ((response as any).id || (response as any).reference) {
        weighingData = response as unknown as Pesée;
      } else {
        console.error('Format de réponse inattendu lors de la récupération des détails de la pesée');
        return;
      }
      
      if (weighingData) {
        // weighingDetails est maintenant géré par le contexte
        // setWeighingDetails(weighingData as unknown as Pesée);
        
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des détails de la pesée:', err);
    }
  };

  // Hook pour gérer le workflow de pesée
  const {
    workflowState,
    formData,
    error: workflowError,
    saveGrossWeight,
    saveTareWeight,
    goToStep,
    nextStep,
    previousStep,
    updateFormData,
    canProceedToNextStep,
    updateWeighingCode,
    resetWorkflow,
    
    // Nouvelles fonctions pour les processus
    getStepLabel,
    getCurrentWeighingType,
    initializeWeighingProcess,
    weighingProcess
  } = useWeighingWorkflow(selectedPontBasculeId);

  // Fonction pour synchroniser complètement les données de la pesée avec le formulaire
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const syncWeighingDataToForm = useCallback((weighing: Pesée) => {
    const typePesee = weighing.type_pesee || 'reception';
    const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';
    
    // Créer un objet de données complet pour la mise à jour en une seule fois
    const newFormData: any = {
      // Champs principaux
      typePesee: typePesee,
      produit: weighing.produit ? weighing.produit.toString() : '',
      conditionnement: weighing.conditionnement ? weighing.conditionnement.toString() : '',
      
      // Véhicule et transport
      vehicule: weighing.camion ? weighing.camion.toString() : '',
      remorque: weighing.remorque ? weighing.remorque.toString() : '',
      transporteur: weighing.transporteur ? weighing.transporteur.toString() : '',
      chauffeur: weighing.chauffeur ? weighing.chauffeur.toString() : '',
      convoyeur: weighing.convoyeur ? weighing.convoyeur.toString() : '',
      
      // Origine et destination
      provenance: weighing.provenance || '',
      connaissement: weighing.connaissement || '',
      
      // Partenaires commerciaux
      client: weighing.client ? weighing.client.toString() : '',
      fournisseur: weighing.fournisseur ? weighing.fournisseur.toString() : '',
      exportateur: weighing.exportateur ? weighing.exportateur.toString() : '',
      
      // Documents et références
      numeroTransfert: weighing.numero_transfert || '',
      sticker: weighing.sticker || '',
      
      // Observations
      observations: weighing.observations || '',
      
      // Champs d'analyse qualité
      nbSacsDeclares: weighing.nb_sacs_declares || 0,
      poidsDeclares: weighing.poids_declares ? parseFloat(weighing.poids_declares.toString()) : 0,
      nbSacsSains: weighing.nb_sacs_sains || 0,
      nbSacsRecond: weighing.nb_sacs_recond || 0,
      nbSacsHumides: weighing.nb_sacs_humides || 0,
      nbSacsDechires: weighing.nb_sacs_dechires || 0,
      nbSacsRejetes: weighing.nb_sacs_rejetes || 0,
      nbSacsAcceptes: weighing.nb_sacs_acceptes || 0,
      poidsAcceptes: weighing.poids_acceptes ? parseFloat(weighing.poids_acceptes.toString()) : 0,
      
      // Champs supplémentaires (valeurs par défaut)
      refractionHum: 0,
      refractionQlt: 0
    };
    
    // Pour les expéditions/transferts, si les valeurs acceptées sont vides, copier depuis déclarées
    if (isExpeditionOrTransfert && workflowState.currentStep === 3) {
      if (newFormData.nbSacsDeclares > 0 && newFormData.nbSacsAcceptes === 0) {
        newFormData.nbSacsAcceptes = newFormData.nbSacsDeclares;
      }
      if (newFormData.poidsDeclares > 0 && newFormData.poidsAcceptes === 0) {
        newFormData.poidsAcceptes = newFormData.poidsDeclares;
      }
    }
    
    // Mettre à jour tous les champs en une seule fois
    Object.entries(newFormData).forEach(([field, value]) => {
      updateFormData(field as keyof typeof formData, value);
    });
    
    // Synchroniser les données de destination
    const destinationData = {
      destination: weighing.destination || '',
      magasin: weighing.magasin ? weighing.magasin.toString() : '',
      numeroLot: weighing.numero_lot || ''
    };
    
    setDestinationFormData(destinationData);
    
    // Synchroniser aussi les champs de destination dans formData
    updateFormData('destination', weighing.destination || '');
    updateFormData('magasin', weighing.magasin ? weighing.magasin.toString() : '');
    updateFormData('numeroLot', weighing.numero_lot || '');
    
    // Synchroniser le poids actuel
    if (weighing.poids_brut) {
      const weight = parseFloat(weighing.poids_brut.toString());
      setCurrentWeight(weight);
    }
  }, [updateFormData, workflowState.currentStep]);

  // Effet pour synchroniser les données entre les étapes
  // Utiliser currentWeighing?.id au lieu de currentWeighing pour éviter les re-renders inutiles
  useEffect(() => {
    // Synchroniser complètement les données quand on navigue entre les étapes
    if (currentWeighing && loadedWeighingId) {
      syncWeighingDataToForm(currentWeighing);
    }
  }, [currentWeighing?.id, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour synchroniser les données à chaque changement d'étape
  useEffect(() => {
    if (currentWeighing && loadedWeighingId) {
      syncWeighingDataToForm(currentWeighing);
    }
  }, [workflowState.currentStep, currentWeighing?.id, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour forcer la synchronisation après le chargement des produits et conditionnements
  const productsLoadedRef = useRef<boolean>(false);
  useEffect(() => {
    if (currentWeighing && loadedWeighingId && products.length > 0 && conditionnements.length > 0 && !productsLoadedRef.current) {
      productsLoadedRef.current = true;
      // Une seule synchronisation après le chargement des produits
      setTimeout(() => {
        syncWeighingDataToForm(currentWeighing);
      }, 200);
    } else if (products.length === 0 || conditionnements.length === 0) {
      productsLoadedRef.current = false;
    }
  }, [products.length, conditionnements.length, currentWeighing?.id, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour forcer la synchronisation quand on arrive à l'étape 1
  const step1SyncedRef = useRef<number>(0);
  useEffect(() => {
    if (workflowState.currentStep === 1 && currentWeighing && loadedWeighingId) {
      const currentWeighingId = typeof currentWeighing.id === 'string' ? parseInt(currentWeighing.id) : (typeof currentWeighing.id === 'number' ? currentWeighing.id : 0);
      if (step1SyncedRef.current !== currentWeighingId) {
        step1SyncedRef.current = currentWeighingId;
        setTimeout(() => {
          syncWeighingDataToForm(currentWeighing);
        }, 200);
      }
    } else if (workflowState.currentStep !== 1) {
      step1SyncedRef.current = 0;
    }
  }, [workflowState.currentStep, currentWeighing?.id, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour forcer la synchronisation quand on arrive à l'étape 3 et copier les valeurs pour expéditions/transferts
  const step3SyncedRef = useRef<{weighingId: number, step: number, copied: boolean}>({weighingId: 0, step: 0, copied: false});
  useEffect(() => {
    if (workflowState.currentStep === 3 && currentWeighing && loadedWeighingId) {
      const currentWeighingId = typeof currentWeighing.id === 'string' ? parseInt(currentWeighing.id) : currentWeighing.id;
      const typePesee = currentWeighing.type_pesee || formData.typePesee || 'reception';
      const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';
      
      // Synchroniser si on n'a pas déjà synchronisé pour cette pesée à cette étape
      if (step3SyncedRef.current.weighingId !== currentWeighingId || step3SyncedRef.current.step !== 3) {
        step3SyncedRef.current = { weighingId: currentWeighingId, step: 3, copied: false };
        
        // Synchroniser les données quand on arrive à l'étape 3
        syncWeighingDataToForm(currentWeighing);
        
        // Après synchronisation, copier les valeurs déclarées vers acceptées pour expéditions/transferts
        if (isExpeditionOrTransfert) {
          setTimeout(() => {
            // Récupérer les valeurs déclarées depuis currentWeighing (source de vérité)
            const nbSacsDeclares = currentWeighing.nb_sacs_declares || 0;
            const poidsDeclares = currentWeighing.poids_declares ? parseFloat(currentWeighing.poids_declares.toString()) : 0;
            
            // Récupérer les valeurs acceptées actuelles
            const nbSacsAcceptes = currentWeighing.nb_sacs_acceptes || 0;
            const poidsAcceptes = currentWeighing.poids_acceptes ? parseFloat(currentWeighing.poids_acceptes.toString()) : 0;
            
            // Copier si les valeurs déclarées existent et les acceptées sont vides
            if (nbSacsDeclares > 0 && nbSacsAcceptes === 0) {
              updateFormData('nbSacsAcceptes', nbSacsDeclares);
              step3SyncedRef.current.copied = true;
            }
            
            if (poidsDeclares > 0 && poidsAcceptes === 0) {
              updateFormData('poidsAcceptes', poidsDeclares);
              step3SyncedRef.current.copied = true;
            }
          }, 600);
        }
      }
    } else if (workflowState.currentStep !== 3) {
      // Réinitialiser le flag si on n'est plus à l'étape 3
      step3SyncedRef.current = { weighingId: 0, step: 0, copied: false };
    }
  }, [workflowState.currentStep, currentWeighing?.id, loadedWeighingId, syncWeighingDataToForm, updateFormData]);

  // Effet pour vérifier et corriger l'état du formulaire après synchronisation
  // Utiliser un ref pour éviter les boucles infinies
  const syncAttemptsRef = useRef<number>(0);
  useEffect(() => {
    if (currentWeighing && loadedWeighingId && syncAttemptsRef.current < 2) {
      // Vérifier si les champs critiques sont vides après synchronisation
      const criticalFieldsEmpty = !formData.produit || !formData.vehicule || !formData.chauffeur;
      
      if (criticalFieldsEmpty) {
        syncAttemptsRef.current += 1;
        // Re-synchroniser après un délai
        setTimeout(() => {
          syncWeighingDataToForm(currentWeighing);
        }, 200);
      } else {
        // Réinitialiser le compteur si les champs sont remplis
        syncAttemptsRef.current = 0;
      }
    }
  }, [formData.produit, formData.vehicule, formData.chauffeur, currentWeighing?.id, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour mettre à jour automatiquement le poids net à chaque modification de currentWeight
  useEffect(() => {

    if (scaleWeight && scaleWeight.gross_weight) {
      // Le poids de la balance a changé
      // Le composant se re-rendra automatiquement
    }
  }, [scaleWeight?.gross_weight, currentWeighing?.poids_brut, workflowState.grossWeight]);

  // Note: Le processus de pesée est initialisé uniquement lors de la création d'une nouvelle pesée
  // via handleNewWeighing ou lors du chargement d'une pesée existante via loadExistingWeighing
  // On ne réinitialise PAS le processus pendant le remplissage du formulaire pour éviter
  // de changer le mode de capture du poids (standard vs inversé)

  // Ne PAS auto-enregistrer le poids à chaque tick de la balance en étape 3 :
  // cela écrasait le poids net (souvent négatif) avant la saisie des sacs.
  useEffect(() => {
    if (workflowState.currentStep === 3 && isConnected && scaleWeight && scaleWeight.weight !== undefined) {
      setCurrentWeight(scaleWeight.weight);
    }
  }, [workflowState.currentStep, isConnected, scaleWeight]);

  // Effet pour copier automatiquement les valeurs déclarées vers acceptées pour expéditions/transferts
  // Ce useEffect sert de backup si la copie dans step3SyncedRef n'a pas fonctionné
  useEffect(() => {
    // Vérifier si on est à l'étape 3 et que le type de pesée est expédition ou transfert
    if (workflowState.currentStep === 3 && currentWeighing) {
      const typePesee = formData.typePesee || currentWeighing?.type_pesee || 'reception';
      const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';
      
      if (isExpeditionOrTransfert) {
        // Créer une clé unique pour cette pesée et cette étape
        const currentWeighingId = typeof currentWeighing.id === 'string' ? parseInt(currentWeighing.id) : currentWeighing.id;
        const copyKey = `${currentWeighingId}-${workflowState.currentStep}-${typePesee}`;
        const lastCopyKey = hasCopiedValuesRef.current.typePesee;
        
        // Copier seulement si on ne l'a pas déjà fait pour cette pesée
        if (copyKey !== lastCopyKey) {
          // Récupérer les valeurs déclarées (priorité à currentWeighing car plus fiable)
          const nbSacsDeclares = currentWeighing?.nb_sacs_declares || formData.nbSacsDeclares || 0;
          const poidsDeclares = currentWeighing?.poids_declares ? parseFloat(currentWeighing.poids_declares.toString()) : (formData.poidsDeclares || 0);
          
          // Récupérer les valeurs acceptées actuelles
          const nbSacsAcceptes = formData.nbSacsAcceptes || currentWeighing?.nb_sacs_acceptes || 0;
          const poidsAcceptes = formData.poidsAcceptes || (currentWeighing?.poids_acceptes ? parseFloat(currentWeighing.poids_acceptes.toString()) : 0);
          
          // Copier si les valeurs déclarées existent et les acceptées sont vides
          if (nbSacsDeclares > 0 && nbSacsAcceptes === 0) {
            updateFormData('nbSacsAcceptes', nbSacsDeclares);
          }
          
          if (poidsDeclares > 0 && poidsAcceptes === 0) {
            updateFormData('poidsAcceptes', poidsDeclares);
          }
          
          // Marquer comme copié
          hasCopiedValuesRef.current = { step: workflowState.currentStep, typePesee: copyKey };
        }
      } else {
        // Réinitialiser le flag si ce n'est pas une expédition/transfert
        hasCopiedValuesRef.current = { step: 0, typePesee: '' };
      }
    } else if (workflowState.currentStep !== 3) {
      // Réinitialiser le flag si on n'est plus à l'étape 3
      hasCopiedValuesRef.current = { step: 0, typePesee: '' };
    }
  }, [workflowState.currentStep, currentWeighing?.id, formData.typePesee, formData.nbSacsDeclares, formData.poidsDeclares, formData.nbSacsAcceptes, formData.poidsAcceptes, updateFormData]);

  // Charger les produits et les informations du pont bascule au montage du composant
  useEffect(() => {
    const initializePage = async () => {
      // D'abord charger le pont bascule par défaut
      if (!selectedPontBasculeId) {
        const defaultPontId = await fetchDefaultPontBascule();
        if (defaultPontId) {
          // Le fetchDefaultPontBascule met déjà à jour selectedPontBasculeId
          // Donc on peut maintenant charger les autres données
          loadProducts();
          loadConditionnements();
          loadPendingWeighingsCount();
          fetchPontBasculeInfo(defaultPontId);
        } else {
          // Charger les autres données même sans pont bascule
          loadProducts();
          loadConditionnements();
          loadPendingWeighingsCount();
        }
      } else {
        // Pont bascule déjà sélectionné, charger les données
        loadProducts();
        loadConditionnements();
        loadPendingWeighingsCount();
        fetchPontBasculeInfo(selectedPontBasculeId);
      }
    };

    initializePage();
  }, [selectedPontBasculeId]);

  // Rafraîchir le nombre de pesées en attente périodiquement
  useEffect(() => {
    const interval = setInterval(() => {
      loadPendingWeighingsCount();
    }, 30000); // Rafraîchir toutes les 30 secondes

    return () => clearInterval(interval);
  }, []);

  // Rafraîchir le nombre de pesées en attente quand une pesée est créée ou modifiée
  useEffect(() => {
    if (currentWeighing?.id) {
      loadPendingWeighingsCount();
    }
  }, [currentWeighing?.id, currentWeighing?.statut]);

  // Vérifier si on doit charger une pesée existante depuis l'URL
  useEffect(() => {
    // Accepter soit 'weighingId' soit 'continue' comme paramètre
    const weighingId = searchParams.get('weighingId') || searchParams.get('continue');
    const stepParam = searchParams.get('step');
    const forcedStep = stepParam ? parseInt(stepParam) : undefined;
    
    if (weighingId && !loadedWeighingId) {
      loadExistingWeighing(weighingId, forcedStep);
    }
  }, [searchParams, loadedWeighingId]);

  // Récupérer les détails de la pesée quand on arrive à l'étape 3 ou 4
  const step3Or4ReloadedRef = useRef<number>(0);
  useEffect(() => {
    // Ne pas recharger si on a explicitement vidé la pesée (ex: clic "Nouvelle pesée")
    if (!currentWeighing && !workflowState.weighingId) {
      step3Or4ReloadedRef.current = 0;
      return;
    }
    if ((workflowState.currentStep === 3 || workflowState.currentStep === 4) && (workflowState.weighingId || currentWeighing?.id)) {
      const weighingId = workflowState.weighingId || currentWeighing?.id;
      if (weighingId) {
        const currentWeighingId = typeof weighingId === 'string' ? parseInt(weighingId) : weighingId;
        
        // Ne recharger qu'une seule fois par pesée et par étape
        if (step3Or4ReloadedRef.current !== currentWeighingId) {
          step3Or4ReloadedRef.current = currentWeighingId;
          
          fetchWeighingDetails(weighingId.toString());
          fetchQualityAnalysis(weighingId.toString());
          
          // Recharger la pesée complète pour avoir analyse_qualite à jour
          weighingService.getPesée(parseInt(weighingId.toString())).then((response) => {
            if (response.success && response.data) {
              setCurrentWeighing(response.data);
              
              // Synchroniser les données après le rechargement
              const weighingData = response.data;
              if (weighingData) {
                setTimeout(() => {
                  syncWeighingDataToForm(weighingData);
                }, 300);
              }
              
              // Mettre à jour qualityAnalysis en fonction des données rechargées
              if (response.data.analyse_qualite) {
                const analyseQualite = response.data.analyse_qualite;
                
                // Vérifier que l'analyse qualité correspond bien à la pesée actuelle
                const analysisPeseeId = analyseQualite.pesee;
                const responseWeighingId = typeof response.data.id === 'string' ? parseInt(response.data.id) : response.data.id;
                
                if (analysisPeseeId && analysisPeseeId !== responseWeighingId) {
                  setQualityAnalysis(undefined);
                  const cleanedWeighing = { ...response.data };
                  cleanedWeighing.analyse_qualite = undefined;
                  setCurrentWeighing(cleanedWeighing);
                } else {
                  // Vérifier si l'analyse qualité a des données valides
                  const hasValidData = (
                    (analyseQualite.grainnage && analyseQualite.grainnage.trim() !== '') ||
                    (analyseQualite.humidite !== undefined && analyseQualite.humidite !== null && analyseQualite.humidite !== 0) ||
                    (analyseQualite.quality !== undefined && analyseQualite.quality !== null && analyseQualite.quality !== 0) ||
                    (analyseQualite.quality_level && analyseQualite.quality_level.trim() !== '') ||
                    (analyseQualite.quality_decision && analyseQualite.quality_decision.trim() !== '')
                  );
                  
                  if (hasValidData) {
                    setQualityAnalysis(analyseQualite);
                  } else {
                    // L'analyse qualité n'a pas de données valides, la retirer
                    setQualityAnalysis(undefined);
                    const cleanedWeighing = { ...response.data };
                    cleanedWeighing.analyse_qualite = undefined;
                    setCurrentWeighing(cleanedWeighing);
                  }
                }
              } else {
                // Pas d'analyse qualité dans les données rechargées
                setQualityAnalysis(undefined);
              }
            }
          }).catch((error) => {
            console.error('Erreur lors du rechargement de la pesée:', error);
          });
        }
      }
    } else if (workflowState.currentStep !== 3 && workflowState.currentStep !== 4) {
      // Réinitialiser le flag si on n'est plus à l'étape 3 ou 4
      step3Or4ReloadedRef.current = 0;
    }
  }, [workflowState.currentStep, workflowState.weighingId, currentWeighing?.id, syncWeighingDataToForm]);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await weighingService.getProduits();
      setProducts(response.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadConditionnements = async () => {
    try {
      setConditionnementsLoading(true);
      // Limiter le nombre de conditionnements chargés et optimiser la requête
      const response = await weighingService.getConditionnements({
        page_size: 100, // Limiter à 100 conditionnements
        statut: 'ACTIF' // Charger seulement les conditionnements actifs
      });
      setConditionnements(response.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des conditionnements:', error);
    } finally {
      setConditionnementsLoading(false);
    }
  };

  // Fonction pour récupérer le nombre de pesées en attente
  const loadPendingWeighingsCount = async () => {
    try {
    
      // Approche optimisée: Récupérer seulement les pesées en attente avec filtres
      const allWeighingsResponse = await weighingService.getPesées({
        page_size: 100, // Réduire le nombre de pesées chargées
        statut: '', // Ne pas filtrer par statut, on filtre côté client
        // Ne pas inclure les relations pour cette requête de comptage
        include_relations: 'false'
      });
      
      if (allWeighingsResponse?.results && Array.isArray(allWeighingsResponse.results)) {
        const pendingWeighings = allWeighingsResponse.results.filter((weighing: any) => {
          // Exclure les pesées terminées (statut COMPLETEE, ACHEVEE, REFOULEE, ANNULEE)
          if (!weighing.statut || ['COMPLETEE', 'ACHEVEE', 'REFOULEE', 'ANNULEE'].includes(weighing.statut)) {
            return false;
          }
          
          // Exclure les pesées qui ont les deux poids enregistrés (pesée complète)
          // Peu importe le processus, si les deux poids sont présents, la pesée est complète
          const hasBothWeights = weighing.poids_brut && weighing.poids_tare;
          if (hasBothWeights) {
            return false; // Pesée complète, ne pas afficher dans les pesées en attente
          }
          
          // Inclure toutes les autres pesées (non terminées et sans les deux poids)
          return true;
        });
        
        setPendingWeighingsCount(pendingWeighings.length);
        
      } else {
        setPendingWeighingsCount(0);
      }
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement du nombre de pesées en attente:', error);
      setPendingWeighingsCount(0);
    }
  };




  // Gérer les changements d'input pour le formulaire de destination
  const handleDestinationInputChange = (field: string, value: any) => {
    setDestinationFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Synchroniser aussi avec formData pour la sauvegarde
    updateFormData(field as keyof typeof formData, value);
  };


  const handleInputChange = (field: string, value: any) => {
    updateFormData(field as any, value);
  };

  // Fonction locale pour vérifier si on peut passer à l'étape suivante (prend en compte les pesées chargées)
  const canProceedToNextStepLocal = (step: number) => {
    const isInvertedProcess = weighingProcess === 'inverted';
    
    switch (step) {
      case 1:
        // En mode inversé, étape 1 = tare, sinon = brut
        const hasRequiredFields = !!(formData.vehicule && formData.produit);
        if (isInvertedProcess) {
          const hasTareWeight = !!(workflowState.tareWeight || currentWeighing?.poids_tare);
          return hasTareWeight && hasRequiredFields;
        } else {
          const hasGrossWeight = !!(workflowState.grossWeight || currentWeighing?.poids_brut);
          return hasGrossWeight && hasRequiredFields;
        }
      case 2:
        return !!workflowState.analysisData;
      case 3:
        // En mode inversé, étape 3 = brut, sinon = tare
        if (isInvertedProcess) {
          return !!(workflowState.grossWeight || currentWeighing?.poids_brut);
        } else {
          return !!(workflowState.tareWeight || currentWeighing?.poids_tare);
        }
      default:
        return false;
    }
  };

  const handleWeightCapture = async (weightData: any) => {
    const weight = weightData.weight || weightData;
    
    // Déterminer le type de poids selon le processus et l'étape
    const isInvertedProcess = weighingProcess === 'inverted';
    const isStep1 = workflowState.currentStep === 1;
    const isStep3 = workflowState.currentStep === 3;
    const isTareStep = isInvertedProcess && isStep1;
    const isBrutStepInverted = isInvertedProcess && isStep3; // En mode inversé, étape 3 = brut
    const weightType = isTareStep ? 'tare' : 'brut';
    
    // En mode inversé à l'étape 3, utiliser saveTareWeight qui gère l'enregistrement du poids brut
    if (isBrutStepInverted) {
      const success = await saveTareWeight(weight);
      if (success) {
        // Mettre à jour le poids actuel affiché dans le header
        setCurrentWeight(weight);
        
        // Sauvegarder les valeurs d'autocomplétion si elles sont remplies
        if (formData.provenance?.trim()) {
          saveProvenance(formData.provenance);
        }
        if (destinationFormData.destination?.trim()) {
          saveDestination(destinationFormData.destination);
        }
        
        return true; // Indiquer le succès
      } else {
        // Afficher un message d'erreur détaillé
        const errorMessage = workflowError || `Échec de l'enregistrement du poids brut`;
        console.error(`❌ Échec de l'enregistrement du poids brut:`, errorMessage);
        return false; // Indiquer l'échec
      }
    }
    
    // Pour les autres cas (étape 1 standard ou étape 1 inversé), utiliser saveGrossWeight
    const weighingData = await saveGrossWeight(weight);
    if (weighingData) {
      // Mettre à jour le poids actuel affiché dans le header
      setCurrentWeight(weight);
      // Mettre à jour currentWeighing avec les détails de la pesée créée
      if (weighingData.id) {
        setLoadedWeighingId(weighingData.id.toString());
        // Le contexte sera mis à jour automatiquement par useWeighingWorkflow
      }
      
      // Sauvegarder les valeurs d'autocomplétion si elles sont remplies
      if (formData.provenance?.trim()) {
        saveProvenance(formData.provenance);
      }
      if (destinationFormData.destination?.trim()) {
        saveDestination(destinationFormData.destination);
      }
      
      return true; // Indiquer le succès
    } else {
      // Afficher un message d'erreur détaillé
      const errorMessage = workflowError || `Échec de l'enregistrement du poids ${weightType}`;
      console.error(`❌ Échec de l'enregistrement du poids ${weightType}:`, errorMessage);
      return false; // Indiquer l'échec
    }
  };

  const handlePontBasculeChange = (pontBasculeId: string) => {
    setSelectedPontBasculeId(pontBasculeId);
    // Recharger les informations du nouveau pont bascule
    fetchPontBasculeInfo(pontBasculeId);
  };


  // Configuration des étapes dynamique selon le processus
  const steps: WeighingStep[] = [
    {
      id: 1,
      title: getStepLabel(1, weighingProcess),
      status: workflowState.currentStep === 1 ? 'active' : 
              workflowState.currentStep > 1 ? 'completed' : 'pending'
    },
    {
      id: 2,
      title: getStepLabel(2, weighingProcess),
      status: workflowState.currentStep === 2 ? 'active' : 
              workflowState.currentStep > 2 ? 'completed' : 'pending'
    },
    {
      id: 3,
      title: getStepLabel(3, weighingProcess),
      status: workflowState.currentStep === 3 ? 'active' : 
              workflowState.currentStep > 3 ? 'completed' : 'pending'
    },
    {
      id: 4,
      title: getStepLabel(4, weighingProcess),
      status: workflowState.currentStep === 4 ? 'active' : 'pending'
    }
  ];

  const handleNextStep = async () => {
    const isInvertedProcess = weighingProcess === 'inverted';
   
    if (workflowState.currentStep === 1) {
      // Vérifier que les champs obligatoires sont remplis
      const hasRequiredFields = !!(formData.vehicule && formData.produit);
      
      // En mode inversé, étape 1 = tare, sinon = brut
      let hasWeight = false;
      if (isInvertedProcess) {
        hasWeight = !!(workflowState.tareWeight || currentWeighing?.poids_tare);
      } else {
        hasWeight = !!(workflowState.grossWeight || currentWeighing?.poids_brut);
      }
      
      if (!hasRequiredFields || !hasWeight) {
        return;
      }
      
      // Toujours passer à l'étape 2 (analyse qualité)
      nextStep();
    } else if (workflowState.currentStep === 2) {
      // L'analyse qualité sera gérée par le composant QualityPage
      // Si on ignore l'étape, mettre à jour le statut de la pesée sans créer d'analyse qualité
      if (currentWeighing?.id) {
        try {
          const process = resolveWeighingProcess(currentWeighing);
          const hasBrut = currentWeighing.poids_brut && parseFloat(String(currentWeighing.poids_brut)) > 0;
          const hasTare = currentWeighing.poids_tare && parseFloat(String(currentWeighing.poids_tare)) > 0;
          // Une pesée déjà en brut ne doit jamais passer en TARE_ENREGISTREE
          const nextStatut = hasBrut && !hasTare
            ? 'EN_ATTENTE_TARE'
            : (process === 'inverted' ? 'TARE_ENREGISTREE' : 'EN_ATTENTE_TARE');
          await weighingService.updatePesée(parseInt(currentWeighing.id.toString()), {
            statut: nextStatut,
            processus: hasBrut && !hasTare ? 'standard' : process,
          });
          
          // Réinitialiser l'état qualityAnalysis car on ignore l'étape
          setQualityAnalysis(undefined);
          
          // Recharger la pesée pour avoir les données à jour
          const updatedWeighing = await weighingService.getPesée(parseInt(currentWeighing.id.toString()));
          
          if (updatedWeighing.success && updatedWeighing.data) {
            // S'assurer que l'analyse qualité n'est pas incluse dans les données rechargées
            // Si la pesée a une analyse_qualite dans les données mais qu'elle n'a pas été créée,
            // on doit la retirer des données
            if (updatedWeighing.data.analyse_qualite) {
              // Vérifier si l'analyse qualité a des données valides
              const analyseQualite = updatedWeighing.data.analyse_qualite;
              
              const hasValidData = (
                (analyseQualite.grainnage && analyseQualite.grainnage.trim() !== '') ||
                (analyseQualite.humidite !== undefined && analyseQualite.humidite !== null && analyseQualite.humidite !== 0) ||
                (analyseQualite.quality !== undefined && analyseQualite.quality !== null && analyseQualite.quality !== 0) ||
                (analyseQualite.quality_level && analyseQualite.quality_level.trim() !== '') ||
                (analyseQualite.quality_decision && analyseQualite.quality_decision.trim() !== '')
              );
              
              if (!hasValidData) {
                // L'analyse qualité n'a pas de données valides, la retirer
                const cleanedWeighing = { ...updatedWeighing.data };
                cleanedWeighing.analyse_qualite = undefined;
                setCurrentWeighing(cleanedWeighing);
                setQualityAnalysis(undefined);
              } else {
                // L'analyse qualité a des données valides, la garder
                setCurrentWeighing(updatedWeighing.data);
                setQualityAnalysis(analyseQualite);
              }
            } else {
              // Pas d'analyse qualité dans les données rechargées
              setCurrentWeighing(updatedWeighing.data);
              setQualityAnalysis(undefined);
            }
          }
        } catch (error) {
          console.error('Erreur lors de la mise à jour du statut:', error);
        }
      }
      
      // Passer à l'étape suivante
      nextStep();
    } else if (workflowState.currentStep === 3) {
      // La pesée sera gérée par le composant WeightCapture
      // En mode inversé, étape 3 = brut, sinon = tare
      nextStep();
    }
  };

  const handlePrevStep = () => {

    previousStep();
    
    // Synchroniser les données après le retour à l'étape précédente
    if (currentWeighing && loadedWeighingId) {
      setTimeout(() => {
       
        syncWeighingDataToForm(currentWeighing);
      }, 300); // Délai plus long pour s'assurer que l'étape a changé et les composants sont prêts
    }
  };

  const handleStepClick = (stepId: number) => {
    // Permettre la navigation vers les étapes précédentes
    if (stepId < workflowState.currentStep) {
      
      goToStep(stepId);
      
      // Synchroniser les données après le changement d'étape
      if (currentWeighing && loadedWeighingId) {
        setTimeout(() => {
          
          syncWeighingDataToForm(currentWeighing);
        }, 300); // Délai plus long pour s'assurer que l'étape a changé et les composants sont prêts
      }
    }
  };

  const handleTareWeightCapture = async (weightData: any) => {
    const weight = weightData.weight || weightData;
    const success = await saveTareWeight(weight);
    if (success) {
      // Mettre à jour le poids actuel affiché dans le header
      setCurrentWeight(weight);
      
      // Calcul du poids net non utilisé ici; il est calculé lors de la sauvegarde complète
      
    }
  };

  // Fonction pour sauvegarder complètement le poids de l'étape 3 (tare standard OU brut inversé)
  const handleSaveTareWeightComplete = async (capturedWeight: number) => {
    try {
      // Récupérer les données des formulaires de l'étape 3 (AVANT les calculs)
      const destinationData = destinationFormData;
      const detailData = formData; // Données du formulaire de détails
      
      // Déterminer si on est en mode inversé (workflow + pesée en base)
      const isInvertedProcess =
        weighingProcess === 'inverted' ||
        resolveWeighingProcess(currentWeighing) === 'inverted';

      const typePesee = currentWeighing?.type_pesee || detailData.typePesee || 'reception';

      const conditionnementCapacite =
        currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
        currentWeighing?.conditionnement_info?.poids_tare ||
        (currentWeighing as any)?.conditionnement_info?.capacite ||
        50;

      const knownBrut = currentWeighing?.poids_brut
        ? parseFloat(currentWeighing.poids_brut.toString())
        : (workflowState.grossWeight || 0);
      const knownTare = currentWeighing?.poids_tare
        ? parseFloat(currentWeighing.poids_tare.toString())
        : (workflowState.tareWeight || 0);

      const { poidsBrut, poidsTare } = resolveBrutTareForStep3({
        isInverted: isInvertedProcess,
        capturedWeight,
        knownBrut,
        knownTare,
      });

      const formSacs = {
        nbSacsSains: detailData.nbSacsSains,
        nbSacsRecond: detailData.nbSacsRecond,
        nbSacsDeclares: detailData.nbSacsDeclares,
        nbSacsAcceptes: detailData.nbSacsAcceptes,
        poidsAcceptes: detailData.poidsAcceptes,
        poidsDeclares: detailData.poidsDeclares,
      };

      const poidsSacs = resolvePoidsSacs({
        typePesee,
        form: formSacs,
        weighing: currentWeighing || undefined,
        conditionnementCapacite,
      });

      const nbSacsAcceptesFinal = resolveNbSacsAcceptes({
        typePesee,
        form: formSacs,
        weighing: currentWeighing || undefined,
      });

      let poidsAcceptesFinal = formSacs.poidsAcceptes || 0;
      if (!poidsAcceptesFinal || poidsAcceptesFinal <= 0) {
        poidsAcceptesFinal = poidsSacs;
      }

      const netWeight = computePoidsNet(poidsBrut, poidsTare, poidsSacs);

      if (poidsBrut <= 0 || poidsTare < 0) {
        alert('Poids brut ou tare manquant / invalide. Impossible de calculer le poids net.');
        return;
      }

      if (poidsBrut <= poidsTare) {
        alert(
          `Poids net invalide : le brut (${poidsBrut} kg) doit être supérieur à la tare (${poidsTare} kg). Vérifiez le mode inversé/standard.`,
        );
        return;
      }

      if (netWeight < 0) {
        alert(
          `Poids net négatif (${netWeight} kg). Brut=${poidsBrut}, Tare=${poidsTare}, Sacs=${poidsSacs}. Corrigez les sacs ou les poids.`,
        );
        return;
      }
      
      // Préparer les données à sauvegarder pour l'étape 3
      // N'envoyer que le poids de l'étape en cours + le net, pour ne pas
      // ré-horodater la 1ère pesée avec la même date/heure que la 2ème.
      const updateData: any = {
        processus: isInvertedProcess ? 'inverted' : 'standard',
        poids_net: netWeight.toString(),
        ...(isInvertedProcess
          ? { poids_brut: poidsBrut.toString() }
          : { poids_tare: poidsTare.toString() }),
        
        // Données de destination/stockage (onglet 1)
        destination: destinationData.destination,
        numero_lot: destinationData.numeroLot,
        
        // Observations
        observations: detailData.observations,
        
        // Compteurs sacs — toujours persistés pour le ticket / historique
        nb_sacs_declares: detailData.nbSacsDeclares || currentWeighing?.nb_sacs_declares || 0,
        poids_declares: (detailData.poidsDeclares || currentWeighing?.poids_declares || 0).toString(),
        nb_sacs_sains: detailData.nbSacsSains || currentWeighing?.nb_sacs_sains || 0,
        nb_sacs_recond: detailData.nbSacsRecond || currentWeighing?.nb_sacs_recond || 0,
        nb_sacs_humides: detailData.nbSacsHumides || currentWeighing?.nb_sacs_humides || 0,
        nb_sacs_dechires: detailData.nbSacsDechires || currentWeighing?.nb_sacs_dechires || 0,
        nb_sacs_rejetes: detailData.nbSacsRejetes || currentWeighing?.nb_sacs_rejetes || 0,
        nb_sacs_acceptes: nbSacsAcceptesFinal,
        poids_acceptes: poidsAcceptesFinal.toString(),
        
        // Inversé : les deux poids sont enregistrés mais la pesée n'est finalisée qu'au récap (Terminer)
        ...(isInvertedProcess && poidsBrut && poidsTare ? { statut: 'BRUT_ENREGISTRE' } : {})
      };
      
      // Ajouter magasin seulement s'il est défini
      if (destinationData.magasin && destinationData.magasin !== '') {
        updateData.magasin = parseInt(destinationData.magasin);
      }
      
      // Appel API pour mettre à jour la pesée
      if (currentWeighing?.id) {
        await weighingService.updatePesee(parseInt(currentWeighing.id), updateData);
        
        // Mettre à jour le contexte avec les nouvelles données
        updateWeighing(updateData);
        
        // Préparer les données pour le modal de récapitulatif
        const summaryData = {
          reference: currentWeighing?.reference,
          poids_brut: poidsBrut,
          poids_tare: poidsTare,
          poids_net: netWeight,
          destination: destinationData.destination,
          numero_lot: destinationData.numeroLot,
          provenance: detailData.provenance,
          connaissement: detailData.connaissement,
          numero_transfert: detailData.numeroTransfert,
          sticker: detailData.sticker,
          observations: detailData.observations,
          nb_sacs_declares: updateData.nb_sacs_declares,
          poids_declares: parseFloat(updateData.poids_declares) || 0,
          nb_sacs_sains: updateData.nb_sacs_sains,
          nb_sacs_recond: updateData.nb_sacs_recond,
          nb_sacs_humides: updateData.nb_sacs_humides,
          nb_sacs_dechires: updateData.nb_sacs_dechires,
          nb_sacs_rejetes: updateData.nb_sacs_rejetes,
          nb_sacs_acceptes: nbSacsAcceptesFinal,
          poids_acceptes: poidsAcceptesFinal,
          produit: currentWeighing?.produit_info?.nom || currentWeighing?.produit,
          vehicule: currentWeighing?.camion_info?.immatriculation || currentWeighing?.camion,
          chauffeur: currentWeighing?.chauffeur_info ? `${currentWeighing.chauffeur_info.nom} ${currentWeighing.chauffeur_info.prenom}` : currentWeighing?.chauffeur,
          fournisseur: currentWeighing?.fournisseur_info?.nom || currentWeighing?.fournisseur,
          client: currentWeighing?.client_info?.nom || currentWeighing?.client
        };
        
        setTareSummaryData(summaryData);
        setShowTareSummaryModal(true);
      }
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde de la tare:', error);
      
      // Afficher les détails de l'erreur
      if (error.response) {
        console.error('Détails de la réponse:', error.response.data);
        console.error('Status:', error.response.status);
        alert(`Erreur ${error.response.status}: ${JSON.stringify(error.response.data)}`);
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        alert('Erreur de connexion. Vérifiez votre connexion réseau.');
      } else {
        console.error('Erreur:', error.message);
        alert(`Erreur: ${error.message}`);
      }
    }
  };


  // Fonctions pour gérer les actions du modal de récapitulatif
  const handleTareSummaryReprendre = async () => {
    try {
      
      // Annuler l'enregistrement de l'étape 3 - remettre les données à leur état précédent
      if (currentWeighing?.id) {
        const isInverted =
          weighingProcess === 'inverted' ||
          resolveWeighingProcess(currentWeighing) === 'inverted';

        const cancelData: Record<string, unknown> = {
          processus: isInverted ? 'inverted' : 'standard',
          // Statut selon le mode (ne jamais forcer EN_ATTENTE_TARE sur une inversée)
          statut: isInverted ? 'TARE_ENREGISTREE' : 'EN_ATTENTE_TARE',
          poids_net: null,
          destination: '',
          numero_lot: '',
          magasin: null,
          observations: '',
          nb_sacs_declares: 0,
          nb_sacs_sains: 0,
          nb_sacs_recond: 0,
          nb_sacs_humides: 0,
          nb_sacs_dechires: 0,
        };

        if (isInverted) {
          // Annuler le brut capturé à l'étape 3, conserver la tare de l'étape 1
          cancelData.poids_brut = null;
        } else {
          // Annuler la tare capturée à l'étape 3, conserver le brut de l'étape 1
          cancelData.poids_tare = null;
        }
        
        await weighingService.updatePesee(parseInt(currentWeighing.id), cancelData as any);
        
        // Mettre à jour le contexte avec les données annulées
        updateWeighing(cancelData as any);
      }
      
      // Fermer le modal et rester sur l'étape 3
      setShowTareSummaryModal(false);
      setTareSummaryData(null);
    } catch (error) {
      alert('Erreur lors de l\'annulation. Veuillez réessayer.');
    }
  };

  const handleTareSummaryTerminer = async () => {
    try {
      // Finaliser la pesée - marquer comme complétée
      if (currentWeighing?.id) {
        const isInverted =
          weighingProcess === 'inverted' || resolveWeighingProcess(currentWeighing) === 'inverted';
        const alreadyFinalized =
          currentWeighing.statut === 'COMPLETEE' || currentWeighing.statut === 'ACHEVEE';

        // Pesées déjà complétées (ex. ancien flux inversé) : pas de PATCH, passage direct au ticket
        if (!alreadyFinalized) {
          const summary = tareSummaryData;
          const poidsBrut = summary?.poids_brut ?? currentWeighing.poids_brut;
          const poidsTare = summary?.poids_tare ?? currentWeighing.poids_tare;

          const updateData: Record<string, unknown> = {
            statut: 'COMPLETEE',
            processus: isInverted ? 'inverted' : currentWeighing.processus || 'standard',
          };

          if (poidsBrut != null) updateData.poids_brut = poidsBrut;
          if (poidsTare != null) updateData.poids_tare = poidsTare;

          if (poidsBrut != null && poidsTare != null) {
            const typePesee = currentWeighing.type_pesee || formData.typePesee || 'reception';
            const formSacs = {
              nbSacsSains: summary?.nb_sacs_sains ?? formData.nbSacsSains,
              nbSacsRecond: summary?.nb_sacs_recond ?? formData.nbSacsRecond,
              nbSacsDeclares: summary?.nb_sacs_declares ?? formData.nbSacsDeclares,
              nbSacsAcceptes: summary?.nb_sacs_acceptes ?? formData.nbSacsAcceptes,
              poidsAcceptes: summary?.poids_acceptes ?? formData.poidsAcceptes,
              poidsDeclares: summary?.poids_declares ?? formData.poidsDeclares,
            };
            const poidsSacs = resolvePoidsSacs({
              typePesee,
              form: formSacs,
              weighing: currentWeighing,
              conditionnementCapacite:
                currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
                currentWeighing?.conditionnement_info?.poids_tare ||
                50,
            });

            updateData.poids_net =
              summary?.poids_net ??
              computePoidsNet(
                parseFloat(String(poidsBrut)),
                parseFloat(String(poidsTare)),
                poidsSacs,
              );
            updateData.poids_acceptes =
              formSacs.poidsAcceptes || poidsSacs || currentWeighing.poids_acceptes || 0;
            updateData.nb_sacs_declares = summary?.nb_sacs_declares ?? formData.nbSacsDeclares;
            updateData.nb_sacs_sains = summary?.nb_sacs_sains ?? formData.nbSacsSains;
            updateData.nb_sacs_recond = summary?.nb_sacs_recond ?? formData.nbSacsRecond;
            updateData.nb_sacs_humides = summary?.nb_sacs_humides ?? formData.nbSacsHumides;
            updateData.nb_sacs_dechires = summary?.nb_sacs_dechires ?? formData.nbSacsDechires;
            updateData.nb_sacs_rejetes = summary?.nb_sacs_rejetes ?? formData.nbSacsRejetes;
            updateData.nb_sacs_acceptes = summary?.nb_sacs_acceptes ?? formData.nbSacsAcceptes;
          }

          await weighingService.updatePesee(parseInt(currentWeighing.id), updateData);
          updateWeighing({ statut: 'COMPLETEE', ...updateData });
        } else {
          updateWeighing({ statut: 'COMPLETEE' });
        }
        
        // Sauvegarder les valeurs d'autocomplétion si elles sont remplies
        if (formData.provenance?.trim()) {
          saveProvenance(formData.provenance);
        }
        if (destinationFormData.destination?.trim()) {
          saveDestination(destinationFormData.destination);
        }
      } else {
        alert('Aucune pesée active à finaliser. Veuillez créer une nouvelle pesée.');
        return;
      }
      
      // Fermer le modal
      setShowTareSummaryModal(false);
      setTareSummaryData(null);
      
      // Passer à l'étape 4 (impression/envoi du reçu)
      nextStep();
      
    } catch (error: unknown) {
      console.error('Erreur lors de la finalisation:', error);
      const err = error as { response?: { status?: number; data?: unknown }; message?: string };
      if (err.response?.data) {
        alert(
          `Erreur lors de la finalisation (${err.response.status ?? ''}): ${JSON.stringify(err.response.data)}`,
        );
      } else {
        alert(err.message || 'Erreur lors de la finalisation. Veuillez réessayer.');
      }
    }
  };

  // Gérer le succès de l'analyse qualité
  const handleQualityAnalysisSuccess = () => {
    // L'analyse qualité est maintenant gérée par la modale dans QualityPage
    // On passe directement à l'étape suivante
    nextStep();
  };

  // Fonction pour annuler une pesée
  const handleCancelWeighing = () => {
    handleNewWeighing('standard'); // Utilise le processus standard par défaut
  };

  // Fonction pour reprendre une pesée en attente
  const handleSelectPendingWeighing = (weighing: Pesée & { targetStep?: number }) => {
    // Charger la pesée existante avec l'étape cible
    if (weighing.id) {
      loadExistingWeighing(weighing.id.toString(), weighing.targetStep);
    }
  };

  // Fonction pour commencer une nouvelle pesée
  const handleNewWeighing = (selectedProcess: WeighingProcess = 'standard') => {
    // D'abord vider le contexte pesée et les refs pour éviter que des effets ne repassent à l'étape 4
    setLoadedWeighingId(null);
    setCurrentWeighing(null);

    // Réinitialiser le workflow en une seule fois (étape 1 + processus, sans ancien weighingId)
    resetWorkflow(selectedProcess);

    // Réinitialiser le poids actuel
    setCurrentWeight(null);

    // Réinitialiser le statut du pont bascule
    setPontBasculeStatus('checking');

    // Réinitialiser les données du formulaire de destination
    setDestinationFormData({
      destination: '',
      magasin: '',
      numeroLot: ''
    });

    // Réinitialiser l'analyse qualité
    setQualityAnalysis(undefined);

    // Réinitialiser les informations du site, tenant et pont bascule
    setSiteInfo(null);
    setTenantInfo(null);
    setPontBasculeInfo(null);

    // Réinitialiser la notification de pesée chargée
    setShowWeighingLoadedNotification(false);

    // Fermer tous les modals
    setShowWeighingDetailsModal(false);
    setShowTareSummaryModal(false);
    setTareSummaryData(null);
    setShowPendingWeighingsModal(false);
  };


  const handlePrint = () => {
    // L'impression est gérée par le composant TicketActions
  };

  const handleExport = () => {
    // L'export PDF est géré par le composant TicketActions
  };

  const handleEmail = () => {
    // L'envoi d'email est géré par le composant TicketActions
  };


  // Fonction pour commencer une nouvelle pesée (remplace handleCompleteWeighing)
  const handleStartNewWeighing = () => {
    // Demander confirmation à l'utilisateur
    const confirmNew = window.confirm(
      'Voulez-vous commencer une nouvelle pesée ? La pesée actuelle est déjà complétée.'
    );

    if (!confirmNew) {
      return;
    }

    // Réinitialiser complètement la page pour une nouvelle pesée
    handleNewWeighing('standard');
  };

  // Fonction pour archiver la pesée
  const handleArchiveWeighing = async (shouldArchive: boolean) => {
    try {
      if (!currentWeighing?.id) {
        alert('Aucune pesée à archiver');
        return;
      }
      
      // Vérifier si la pesée est déjà archivée
      if (currentWeighing.statut === 'ACHEVEE' || currentWeighing.statut === 'COMPLETEE') {
        alert('Cette pesée est déjà archivée ou complétée.');
        return;
      }
      
      if (shouldArchive) {
        // Appeler le service pour archiver la pesée
        const response = await weighingService.archiveWeighing(
          parseInt(currentWeighing.id),
          'Archivage manuel depuis l\'interface'
        );

        if (response.success) {
          alert('Pesée archivée avec succès !');
          
          // Réinitialiser complètement la page pour une nouvelle pesée
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erreur lors de l\'archivage');
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      // Vérifier si l'erreur indique que la pesée est déjà archivée
      if (errorMessage.includes('déjà archivé') || errorMessage.includes('already archived') || errorMessage.includes('ACHEVEE')) {
        alert('Cette pesée est déjà archivée.');
      } else {
        alert(`Erreur lors de l'archivage de la pesée: ${errorMessage}`);
      }
    }
  };

  // Le calcul du poids net est maintenant fait directement dans getTicketData()


  // Données du ticket pour l'étape 4 - Utilise maintenant la fonction centralisée
  const getTicketData = (): WeighingTicketData => {
    if (!currentWeighing) {
      // Fallback si aucune pesée n'est chargée
      return convertPeseeToTicketData({
        id: workflowState.weighingId || '',
        reference: workflowState.weighingCode || '',
        poids_brut: workflowState.grossWeight || 0,
        poids_tare: workflowState.tareWeight || 0,
        poids_net: (workflowState.grossWeight || 0) - (workflowState.tareWeight || 0),
        date_heure: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as any, {
        formData,
        siteInfo,
        tenantInfo,
        pontBasculeInfo,
        qualityAnalysis
      });
    }
    
    // Vérifier que qualityAnalysis correspond bien à la pesée actuelle
    let validQualityAnalysis = qualityAnalysis;
    if (qualityAnalysis && currentWeighing?.id) {
      const qualityAnalysisPeseeId = qualityAnalysis.pesee;
      const currentWeighingId = typeof currentWeighing.id === 'string' ? parseInt(currentWeighing.id) : currentWeighing.id;
      
      if (qualityAnalysisPeseeId && qualityAnalysisPeseeId !== currentWeighingId) {
        validQualityAnalysis = undefined;
      }
    } else if (qualityAnalysis && !currentWeighing?.id) {
      validQualityAnalysis = undefined;
    }
    
    // Utiliser la fonction centralisée pour garantir la cohérence
    const ticketData = convertPeseeToTicketData(currentWeighing, {
      formData,
      siteInfo,
      tenantInfo,
      pontBasculeInfo,
      qualityAnalysis: validQualityAnalysis
    });
    
    return ticketData;
  };

  // Préparer les options des produits pour le SearchableSelect
  const productOptions = products.map(product => ({
    value: product.id.toString(),
    label: `${product.nom} - ${product.type_display || product.type || ''}`
  }));

  // Préparer les options des conditionnements pour le SearchableSelect
  const conditionnementOptions = conditionnements.map(conditionnement => ({
    value: conditionnement.id.toString(),
    label: `${conditionnement.nom} - ${conditionnement.poids || ''}kg`
  }));


  return (
    <PermissionGuard 
      permission={PERMISSIONS.WEIGHING_VIEW}
      showError={true}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Informations de la pesée actuelle */}
        <CurrentWeighingInfo className="m-4" />
      
      {/* Header simplifié */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="p-2 text-gray-400 hover:text-gray-600 mr-4">
              <i className="fas fa-arrow-left"></i>
            </button>
        <HeaderTabs 
          onNewWeighing={handleNewWeighing}
          disabled={workflowState.currentStep > 1 && !currentWeighing}
          defaultProcess="standard"
        />
        
      
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-6">
              {/* État du pont bascule */}
              
              
              {/* Poids actuel */}
              <div>
                <p className="text-sm text-gray-600">Poids actuel</p>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-green-600 mr-2">
                    {currentWeight ? `${currentWeight.toLocaleString()} kg` : '-- kg'}
                  </span>
                  <div className={`w-3 h-3 rounded-full ${currentWeight ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stepper avec indicateur de pesée en modification */}
        <div className="mb-6">
         
          
          <WeighingStepper
            steps={steps}
            currentStep={workflowState.currentStep}
            onStepClick={handleStepClick}
            className="mb-6"
          />
        </div>

        {/* Messages d'erreur */}
        {workflowError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{workflowError}</p>
          </div>
        )}

        {/* Indicateur de chargement d'une pesée existante - Masque tout le contenu */}
        {isLoadingExistingWeighing ? (
          <div className="fixed inset-0 bg-white z-[5000] flex items-center justify-center">
            <div className="text-center">
              <div className="relative mb-8">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 mx-auto"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-1/2 transform -translate-x-1/2"></div>
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-blue-800">
                  Redirection en cours...
                </h2>
                <p className="text-lg text-blue-600 max-w-md mx-auto">
                  {loadingStep || 'Chargement de la pesée existante et redirection vers l\'étape appropriée'}
                </p>
                <div className="flex justify-center space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
                <div className="w-64 bg-blue-200 rounded-full h-3 mx-auto">
                  <div className="bg-blue-600 h-3 rounded-full animate-pulse" style={{width: '60%'}}></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Notification de pesée chargée */}
            {showWeighingLoadedNotification && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <i className="fas fa-edit text-blue-600 mr-3 text-lg"></i>
                    <div>
                      <p className="text-sm text-blue-800 font-medium">
                        Mode modification - Pesée chargée avec succès !
                      </p>
                      <p className="text-xs text-blue-600">
                        Référence: {currentWeighing?.reference} - Étape {workflowState.currentStep} - Toutes les données sont pré-remplies
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowWeighingLoadedNotification(false)}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6">
              {/*<div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {workflowState.currentStep === 1 && `${getStepLabel(1, weighingProcess)} - Étape 1/4`}
                    {workflowState.currentStep === 2 && `${getStepLabel(2, weighingProcess)} - Étape 2/4`}
                    {workflowState.currentStep === 3 && `${getStepLabel(3, weighingProcess)} - Étape 3/4`}
                    {workflowState.currentStep === 4 && `${getStepLabel(4, weighingProcess)} - Étape 4/4`}
                  </h2>
                  
               
                  {weighingProcess === 'inverted' && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                      <i className="fas fa-exchange-alt mr-1"></i>
                      Mode inversé
                    </span>
                  )}
                  {weighingProcess === 'standard' && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      <i className="fas fa-arrow-right mr-1"></i>
                      Mode standard
                    </span>
                  )}
                </div>
                
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  workflowState.currentStep === 1 ? 'bg-green-100 text-green-800' :
                  workflowState.currentStep === 2 ? 'bg-blue-100 text-blue-800' :
                  workflowState.currentStep === 3 ? 'bg-purple-100 text-purple-800' :
                  'bg-orange-100 text-orange-800'
                }`}>
                  Étape {workflowState.currentStep}/4
                </span>
              </div>*/}

              {workflowState.currentStep === 1 && (
                // Étape 1: Formulaire avec onglets
                <div className="space-y-6">
                  {/* Type de Pesée, Produit et Conditionnement */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <SearchableSelect
                        value={formData.typePesee}
                        onChange={(value) => handleInputChange('typePesee', value)}
                        options={[
                          { value: 'reception', label: 'Réception' },
                          { value: 'expedition', label: 'Expédition' },
                          { value: 'transfert', label: 'Transfert' }
                        ]}
                        placeholder="Sélectionner le type"
                        label="Type *"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <SearchableSelect
                        value={formData.produit}
                        onChange={(value) => handleInputChange('produit', value)}
                        options={productOptions}
                        placeholder={productsLoading ? "Chargement..." : "Sélectionner un produit"}
                        label="Produit *"
                        className="w-full"
                        disabled={productsLoading}
                      />
                      {productsLoading && (
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                          Chargement des produits...
                        </div>
                      )}
                    </div>
                    <div>
                      <SearchableSelect
                        value={formData.conditionnement}
                        onChange={(value) => handleInputChange('conditionnement', value)}
                        options={conditionnementOptions}
                        placeholder={conditionnementsLoading ? "Chargement..." : "Sélectionner un conditionnement"}
                        label="Sac/conditionnement"
                        className="w-full"
                        disabled={conditionnementsLoading}
                      />
                      {conditionnementsLoading && (
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                          Chargement des conditionnements...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section unifiée - Informations Véhicule et Acteurs */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 shadow-lg mb-4">
                    <div className="p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Colonne gauche - Informations Véhicule */}
                        <div>
                          <VehicleInfoSection 
                            formData={{
                              vehicule: formData.vehicule,
                              remorque: formData.remorque,
                              transporteur: formData.transporteur,
                              chauffeur: formData.chauffeur,
                              convoyeur: formData.convoyeur
                            }}
                            onInputChange={handleInputChange}
                          />
                        </div>
                        
                        {/* Colonne droite - Informations Acteurs */}
                        <div>
                          <ClientSupplierSection 
                            formData={{
                              client: formData.client,
                              fournisseur: formData.fournisseur,
                              exportateur: formData.exportateur
                            }}
                            onInputChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Section unifiée - Déclarations et Documents */}
                  <div className="bg-white border border-orange-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Colonne gauche - Champs de déclaration */}
                      <div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nb de sacs déclarés
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={formData.nbSacsDeclares || ''}
                              onChange={(e) => handleInputChange('nbSacsDeclares', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              placeholder="Nombre de sacs déclarés"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Poids déclarés (kg)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formData.poidsDeclares || ''}
                              onChange={(e) => handleInputChange('poidsDeclares', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              placeholder="Poids déclaré en kg"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne droite - Documents et Références */}
                      <div>
                        <TransferStickerSection 
                          formData={{
                            numeroTransfert: formData.numeroTransfert,
                            sticker: formData.sticker
                          }}
                          onInputChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Capture du poids - Intégré avec sélection de pont bascule */}
                  <WeightCapture
                    siteId={user?.sites?.[0]?.id}
                    onWeightCaptured={handleWeightCapture}
                    onWeightChange={setCurrentWeight}
                    onStatusChange={setPontBasculeStatus}
                    onPontBasculeChange={handlePontBasculeChange}
                    mode={getCurrentWeighingType(1, weighingProcess) === 'tare' ? 'tare' : 'gross'}
                    hasExistingWeight={(() => {
                      const hasWeight = !!(workflowState.grossWeight || currentWeighing?.poids_brut);
                      return hasWeight;
                    })()}
                    existingWeight={currentWeighing?.poids_brut || workflowState.grossWeight}
                    className="mb-6"
                  />

                  {/* Affichage du code de pesée si créé */}
                  {workflowState.weighingCode && (
                    <WeighingCodeDisplay
                      weighingCode={getDisplayPeseeCode(currentWeighing) || workflowState.weighingCode}
                      weighingId={workflowState.weighingId}
                      tenantId={workflowState.tenantId}
                      siteId={workflowState.siteId}
                      className="mb-6"
                    />
                  )}

                  {/* Boutons d'action */}
                  <div className="flex justify-between pt-4">
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_CANCEL}
                      onClick={handleCancelWeighing}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      disabledMessage="Permission insuffisante pour annuler une pesée"
                    >
                      <i className="fas fa-times mr-2"></i>Annuler
                    </PermissionButton>
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_CREATE}
                      onClick={() => {
                        handleNextStep();
                      }}
                      disabled={!canProceedToNextStepLocal(1)}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabledMessage="Permission insuffisante pour créer une pesée"
                    >
                      <i className="fas fa-arrow-right mr-2"></i>Étape suivante
                    </PermissionButton>
                  </div>
                </div>
              )}

                {workflowState.currentStep === 2 && (
                  <div className="space-y-6">
                    <QualityPage
                      currentWeighing={currentWeighing || undefined}
                      onNextStep={handleQualityAnalysisSuccess}
                      onPrevStep={handlePrevStep}
                    />
                  </div>
                )}

               {workflowState.currentStep === 3 && (
                // Étape 3: Poids Tare avec onglets pour destination et détails
                <div className="space-y-6">
                  {/* Section unifiée - Informations de la pesée et analyse qualité */}
                  <div className="bg-blue-50 rounded-lg p-6 mb-6 shadow-md">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <i className="fas fa-info-circle text-blue-600 mr-3 text-xl"></i>
                        <h3 className="text-xl font-semibold text-gray-900">Informations de la pesée</h3>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm font-medium">
                        #{currentWeighing ? getDisplayPeseeCode(currentWeighing) : 'N/A'}
                      </span>
                    </div>
                    
                    {/* Grille des informations principales */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {/* Produit */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-box text-orange-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Produit</span>
                        </div>
                        <p className="text-orange-600 font-semibold text-base">
                          {currentWeighing?.produit_info?.nom || currentWeighing?.produit || formData.produit || ''}
                        </p>
                      </div>

                      {/* Véhicule */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-truck text-green-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Véhicule</span>
                        </div>
                        <p className="text-green-600 font-semibold text-base">
                          {currentWeighing?.camion_info?.immatriculation || currentWeighing?.camion || formData.vehicule || 'N/A'}
                        </p>
                      </div>

                      {/* Poids brut */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-weight text-purple-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Poids brut</span>
                        </div>
                        <p className="text-purple-600 font-semibold text-base">
                          {currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()).toLocaleString() : (workflowState.grossWeight ? parseFloat(workflowState.grossWeight.toString()).toLocaleString() : 'N/A')} kg
                        </p>
                      </div>

                      {/* Date pesée */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-calendar text-purple-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Date pesée</span>
                        </div>
                        <p className="text-purple-600 font-semibold text-base">
                          {currentWeighing?.date_heure ? new Date(currentWeighing.date_heure).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    {/* Bouton pour afficher toutes les informations */}
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => setShowWeighingDetailsModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors border border-blue-200"
                        title="Voir toutes les informations de la pesée"
                      >
                        <i className="fas fa-info-circle mr-2"></i>
                        <span className="text-sm font-medium">Détails complets</span>
                      </button>
                    </div>




                  </div>
                   
                    {/* Section d'analyse qualité des sacs */}
                  <div className="mt-6">
                    <QualityAnalysisSection 
                      formData={{
                        nbSacsDeclares: formData.nbSacsDeclares || currentWeighing?.nb_sacs_declares || 0,
                        poidsDeclares: formData.poidsDeclares || (currentWeighing?.poids_declares ? parseFloat(currentWeighing.poids_declares.toString()) : 0),
                        nbSacsSains: formData.nbSacsSains || currentWeighing?.nb_sacs_sains || 0,
                        nbSacsRecond: formData.nbSacsRecond || currentWeighing?.nb_sacs_recond || 0,
                        nbSacsHumides: formData.nbSacsHumides || currentWeighing?.nb_sacs_humides || 0,
                        nbSacsDechires: formData.nbSacsDechires || currentWeighing?.nb_sacs_dechires || 0,
                        nbSacsRejetes: formData.nbSacsRejetes || currentWeighing?.nb_sacs_rejetes || 0,
                        nbSacsAcceptes: formData.nbSacsAcceptes || currentWeighing?.nb_sacs_acceptes || 0,
                        poidsAcceptes: formData.poidsAcceptes || (currentWeighing?.poids_acceptes ? parseFloat(currentWeighing.poids_acceptes.toString()) : 0),
                        observations: formData.observations || currentWeighing?.observations || ''
                      }}
                      onInputChange={handleInputChange}
                      conditionnementCapacite={
                        currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
                        currentWeighing?.conditionnement_info?.poids_tare ||
                        50
                      }
                      typePesee={formData.typePesee || currentWeighing?.type_pesee || 'reception'}
                    />
                  </div>

                  {/* Capture du poids - Intégré avec sélection de pont bascule */}
                  <WeightCapture
                    siteId={user?.sites?.[0]?.id}
                    onWeightCaptured={handleTareWeightCapture}
                    onWeightChange={setCurrentWeight}
                    onStatusChange={setPontBasculeStatus}
                    mode={getCurrentWeighingType(3, weighingProcess) === 'tare' ? 'tare' : 'gross'}
                    grossWeight={(() => {
                      // Poids de référence déjà connu :
                      // - inversé : tare (étape 1)
                      // - standard : brut (étape 1)
                      if (weighingProcess === 'inverted' || resolveWeighingProcess(currentWeighing) === 'inverted') {
                        return currentWeighing?.poids_tare ? parseFloat(currentWeighing.poids_tare.toString()) : (workflowState.tareWeight || 0);
                      }
                      return currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()) : (workflowState.grossWeight || 0);
                    })()}
                    calculatedBagWeight={resolvePoidsSacs({
                      typePesee: formData.typePesee || currentWeighing?.type_pesee,
                      form: {
                        nbSacsSains: formData.nbSacsSains,
                        nbSacsRecond: formData.nbSacsRecond,
                        nbSacsDeclares: formData.nbSacsDeclares,
                        nbSacsAcceptes: formData.nbSacsAcceptes,
                        poidsAcceptes: formData.poidsAcceptes,
                        poidsDeclares: formData.poidsDeclares,
                      },
                      weighing: currentWeighing || undefined,
                      conditionnementCapacite:
                        currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
                        currentWeighing?.conditionnement_info?.poids_tare ||
                        50,
                    })}
                    onSaveTareWeight={handleSaveTareWeightComplete}
                    hasExistingWeight={(() => {
                      if (weighingProcess === 'inverted' || resolveWeighingProcess(currentWeighing) === 'inverted') {
                        return !!(currentWeighing?.poids_brut || workflowState.grossWeight);
                      }
                      return !!(currentWeighing?.poids_tare || workflowState.tareWeight);
                    })()}
                    className="mb-6"
                  />

                  {/* Section discrète pour afficher le poids sac et le poids net (standard et inversé) */}
                  <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <i className="fas fa-calculator text-gray-400 mr-2 text-sm"></i>
                        <h4 className="text-sm font-medium text-gray-600">Calcul du poids net</h4>
                      </div>
                      {weighingProcess === 'inverted' && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">
                          Mode inversé
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Poids Brut (pour standard) ou Tare (pour inversé) */}
                      <div className="bg-white rounded p-3 border border-gray-200">
                        <div className="flex items-center mb-1">
                          <i className="fas fa-weight text-gray-400 mr-2 text-xs"></i>
                          <span className="text-xs text-gray-500">
                            {weighingProcess === 'inverted' ? 'Poids Tare' : 'Poids Brut'}
                          </span>
                        </div>
                        <p className="text-gray-700 font-semibold text-lg">
                          {(() => {
                            if (weighingProcess === 'inverted') {
                              const tareWeight = currentWeighing?.poids_tare ? parseFloat(currentWeighing.poids_tare.toString()) : (workflowState.tareWeight || 0);
                              return tareWeight > 0 ? `${tareWeight.toLocaleString()} kg` : '-- kg';
                            } else {
                              const grossWeight = currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()) : (workflowState.grossWeight || 0);
                              return grossWeight > 0 ? `${grossWeight.toLocaleString()} kg` : '-- kg';
                            }
                          })()}
                        </p>
                      </div>

                      {/* Poids Sac */}
                      <div className="bg-white rounded p-3 border border-gray-200">
                        <div className="flex items-center mb-1">
                          <i className="fas fa-shopping-bag text-gray-400 mr-2 text-xs"></i>
                          <span className="text-xs text-gray-500">Poids Sac</span>
                        </div>
                        <p className="text-gray-700 font-semibold text-lg">
                          {(() => {
                            const poidsSacs = resolvePoidsSacs({
                              typePesee: formData.typePesee || currentWeighing?.type_pesee,
                              form: {
                                nbSacsSains: formData.nbSacsSains,
                                nbSacsRecond: formData.nbSacsRecond,
                                nbSacsDeclares: formData.nbSacsDeclares,
                                nbSacsAcceptes: formData.nbSacsAcceptes,
                                poidsAcceptes: formData.poidsAcceptes,
                                poidsDeclares: formData.poidsDeclares,
                              },
                              weighing: currentWeighing || undefined,
                              conditionnementCapacite:
                                currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
                                currentWeighing?.conditionnement_info?.poids_tare ||
                                50,
                            });
                            return poidsSacs > 0 ? `${poidsSacs.toLocaleString()} kg` : '-- kg';
                          })()}
                        </p>
                      </div>

                      {/* Poids Net Calculé */}
                      <div className="bg-white rounded p-3 border border-green-200 bg-green-50">
                        <div className="flex items-center mb-1">
                          <i className="fas fa-check-circle text-green-500 mr-2 text-xs"></i>
                          <span className="text-xs text-gray-500">Poids Net</span>
                        </div>
                        <p className={`font-bold text-xl ${
                          (() => {
                            const isInverted =
                              weighingProcess === 'inverted' ||
                              resolveWeighingProcess(currentWeighing) === 'inverted';
                            const { poidsBrut, poidsTare } = resolveBrutTareForStep3({
                              isInverted,
                              capturedWeight: currentWeight || 0,
                              knownBrut: currentWeighing?.poids_brut
                                ? parseFloat(currentWeighing.poids_brut.toString())
                                : workflowState.grossWeight,
                              knownTare: currentWeighing?.poids_tare
                                ? parseFloat(currentWeighing.poids_tare.toString())
                                : workflowState.tareWeight,
                            });
                            const poidsSacs = resolvePoidsSacs({
                              typePesee: formData.typePesee || currentWeighing?.type_pesee,
                              form: {
                                nbSacsSains: formData.nbSacsSains,
                                nbSacsRecond: formData.nbSacsRecond,
                                nbSacsDeclares: formData.nbSacsDeclares,
                                nbSacsAcceptes: formData.nbSacsAcceptes,
                                poidsAcceptes: formData.poidsAcceptes,
                                poidsDeclares: formData.poidsDeclares,
                              },
                              weighing: currentWeighing || undefined,
                              conditionnementCapacite:
                                currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
                                currentWeighing?.conditionnement_info?.poids_tare ||
                                50,
                            });
                            const netWeight =
                              poidsBrut > 0 && poidsTare >= 0
                                ? computePoidsNet(poidsBrut, poidsTare, poidsSacs)
                                : null;
                            return netWeight !== null && netWeight < 0 ? 'text-red-600' : 'text-green-600';
                          })()
                        }`}>
                          {(() => {
                            const isInverted =
                              weighingProcess === 'inverted' ||
                              resolveWeighingProcess(currentWeighing) === 'inverted';
                            const { poidsBrut, poidsTare } = resolveBrutTareForStep3({
                              isInverted,
                              capturedWeight: currentWeight || 0,
                              knownBrut: currentWeighing?.poids_brut
                                ? parseFloat(currentWeighing.poids_brut.toString())
                                : workflowState.grossWeight,
                              knownTare: currentWeighing?.poids_tare
                                ? parseFloat(currentWeighing.poids_tare.toString())
                                : workflowState.tareWeight,
                            });
                            const poidsSacs = resolvePoidsSacs({
                              typePesee: formData.typePesee || currentWeighing?.type_pesee,
                              form: {
                                nbSacsSains: formData.nbSacsSains,
                                nbSacsRecond: formData.nbSacsRecond,
                                nbSacsDeclares: formData.nbSacsDeclares,
                                nbSacsAcceptes: formData.nbSacsAcceptes,
                                poidsAcceptes: formData.poidsAcceptes,
                                poidsDeclares: formData.poidsDeclares,
                              },
                              weighing: currentWeighing || undefined,
                              conditionnementCapacite:
                                currentWeighing?.produit_info?.conditionnement_info?.poids_tare ||
                                currentWeighing?.conditionnement_info?.poids_tare ||
                                50,
                            });
                            const netWeight =
                              poidsBrut > 0 && poidsTare >= 0
                                ? computePoidsNet(poidsBrut, poidsTare, poidsSacs)
                                : null;
                            if (netWeight === null) return '-- kg';
                            return `${netWeight.toLocaleString()} kg`;
                          })()}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Formule : Poids Brut − (Poids Tare + Poids Sacs)
                    </p>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex justify-between">
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_EDIT}
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      disabledMessage="Permission insuffisante pour modifier une pesée"
                    >
                      <i className="fas fa-arrow-left mr-2"></i>Retour étape 2
                    </PermissionButton>
                    
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_COMPLETE}
                      onClick={handleNextStep}
                      disabled={!canProceedToNextStep(3)}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabledMessage="Permission insuffisante pour compléter une pesée"
                    >
                      <i className="fas fa-arrow-right mr-2"></i>Voir le ticket
                    </PermissionButton>
                  </div>
                </div>
              )}

              {workflowState.currentStep === 4 && (
                // Étape 4: Ticket final
                <div>
                  <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">Ticket de Pesée</h3>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        Pesée terminée
                      </span>
                    </div>
                    
                    {/* Informations complètes du ticket */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">Résumé complet de la pesée</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        {/* Informations générales */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800 border-b border-gray-300 pb-1">Informations générales</h5>
                      <div>
                            <span className="text-gray-600">Référence:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().code}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Date:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().weighingDate || new Date().toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Année campagne:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().campaignYear}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Produit:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().product}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Nature produit:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().productNature || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Origine:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().productOrigin || 'N/A'}</p>
                          </div>
                        </div>
                        
                        {/* Véhicule et transport */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800 border-b border-gray-300 pb-1">Véhicule & Transport</h5>
                          <div>
                            <span className="text-gray-600">Véhicule:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().vehicle}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Chauffeur:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().driver}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Remorque:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().trailerRegistration || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Transporteur:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().transporterName || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Provenance:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().provenance || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Destination:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().destination || 'N/A'}</p>
                        </div>
                      </div>
                      
                        {/* Poids et qualité */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800 border-b border-gray-300 pb-1">Poids & Qualité</h5>
                      <div>
                            <span className="text-gray-600">Poids brut:</span>
                            <p className="font-semibold text-blue-600 text-lg">{getTicketData().grossWeight.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Poids tare:</span>
                            <p className="font-semibold text-orange-600 text-lg">{getTicketData().tareWeight.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Poids net:</span>
                            <p className="font-semibold text-green-600 text-xl">{getTicketData().netWeight.toLocaleString()} kg</p>
                          </div>
                          {(() => {
                            const ticketData = getTicketData();
                            const analyseQualite = ticketData.analyse_qualite;
                            
                            if (!analyseQualite) {
                              return null;
                            }
                            
                            return (
                              <>
                                <div>
                                  <span className="text-gray-600">Taux humidité:</span>
                                  <p className="font-semibold text-gray-900">{analyseQualite.humidite !== undefined && analyseQualite.humidite !== null ? `${analyseQualite.humidite}%` : (ticketData.humidityRateKOR || 'N/A')}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Niveau de qualité:</span>
                                  <p className="font-semibold text-gray-900">{analyseQualite.quality_level || ticketData.qualityLevel || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Grainnage:</span>
                                  <p className="font-semibold text-gray-900">{analyseQualite.grainnage || ticketData.grainnage || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Qualité:</span>
                                  <p className="font-semibold text-gray-900">{analyseQualite.quality !== undefined && analyseQualite.quality !== null ? analyseQualite.quality : (ticketData.quality || 'N/A')}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Humidité:</span>
                                  <p className="font-semibold text-gray-900">{analyseQualite.humidite !== undefined && analyseQualite.humidite !== null ? `${analyseQualite.humidite}%` : (ticketData.humidite ? `${ticketData.humidite}%` : 'N/A')}</p>
                                </div>
                              </>
                            );
                          })()}
                          <div>
                            <span className="text-gray-600">Réfraction qualité:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().qualityRefraction || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Type conditionnement:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().packagingType || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Informations partenaires */}
                      <div className="mt-6 pt-4 border-t border-gray-300">
                        <h5 className="font-medium text-gray-800 mb-4">Partenaires</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Fournisseur:</span>
                            <p className="font-semibold text-gray-900">
                              {getTicketData().supplierIdentity || 'N/A'}
                              {getTicketData().supplierCode ? `/${getTicketData().supplierCode}` : ''}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Client:</span>
                            <p className="font-semibold text-gray-900">
                              {getTicketData().clientCode || 'N/A'}
                              {getTicketData().clientCodeValue ? `/${getTicketData().clientCodeValue}` : ''}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600">Exportateur:</span>
                            <p className="font-semibold text-gray-900">
                              {(getTicketData().exporterName || getTicketData().exporterCode)
                                ? `${getTicketData().exporterName || ''}${getTicketData().exporterCode ? `/${getTicketData().exporterCode}` : ''}`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Documents et références */}
                      <div className="mt-6 pt-4 border-t border-gray-300">
                        <h5 className="font-medium text-gray-800 mb-4">Documents & Références</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">N° Lot:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().bushLotNumber || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">N° Transfert:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().transferSheetNumber || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">N° Sticker:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().stickerNumber || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Observations:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().observations || 'Aucune'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions du ticket */}
                    <div className="flex justify-center">
                      <TicketActions
                        ticketData={getTicketData()}
                        onPrint={handlePrint}
                        onExport={handleExport}
                        onEmail={handleEmail}
                        variant="default"
                        showLabels={true}
                      />
                    </div>
                  </div>
                  
                  {/* Bouton pour commencer une nouvelle pesée */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-center space-x-4">
                      <PermissionButton
                        permission={PERMISSIONS.WEIGHING_CREATE}
                        onClick={handleStartNewWeighing}
                        className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center font-medium"
                        title="Commencer une nouvelle pesée"
                        disabledMessage="Permission insuffisante pour créer une pesée"
                      >
                        <i className="fas fa-plus-circle mr-2"></i>
                        Nouvelle pesée
                      </PermissionButton>
                      
                      <PermissionButton
                        permission={PERMISSIONS.WEIGHING_COMPLETE}
                        onClick={() => {
                          const confirmArchive = window.confirm(
                            'Êtes-vous sûr de vouloir archiver cette pesée ? Cette action verrouillera les modifications.'
                          );
                          if (confirmArchive) {
                            handleArchiveWeighing(true);
                          }
                        }}
                        className="px-8 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                        title={currentWeighing?.statut === 'ACHEVEE' || currentWeighing?.statut === 'COMPLETEE' 
                          ? 'Cette pesée est déjà archivée' 
                          : 'Archiver la pesée (verrouille les modifications)'}
                        disabledMessage={currentWeighing?.statut === 'ACHEVEE' || currentWeighing?.statut === 'COMPLETEE'
                          ? 'Cette pesée est déjà archivée'
                          : 'Permission insuffisante pour archiver une pesée'}
                        disabled={currentWeighing?.statut === 'ACHEVEE' || currentWeighing?.statut === 'COMPLETEE'}
                      >
                        <i className="fas fa-archive mr-2"></i>
                        {currentWeighing?.statut === 'ACHEVEE' || currentWeighing?.statut === 'COMPLETEE' 
                          ? 'Pesée archivée' 
                          : 'Archiver la pesée'}
                      </PermissionButton>
                    </div>
                    <p className="text-center text-sm text-gray-600 mt-2">
                      La pesée est complétée. Cliquez pour commencer une nouvelle pesée ou archiver celle-ci.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

                     {/* Sidebar */}
           <div className="lg:col-span-1 space-y-4">
             {/* Bouton Pesées en attente */}
             <PermissionGuard 
               permission={PERMISSIONS.WEIGHING_VIEW}
               fallback={
                 <div className="bg-white rounded-xl shadow-sm p-6">
                   <button 
                     disabled
                     className="w-full px-4 py-3 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed"
                   >
                     <i className="fas fa-clock mr-2"></i>
                     Pesées en attente (0)
                   </button>
                 </div>
               }
             >
               <div className="bg-white rounded-xl shadow-sm p-6">
                 <button 
                   onClick={() => {
                     loadPendingWeighingsCount(); // Rafraîchir le count avant d'ouvrir
                     setShowPendingWeighingsModal(true);
                   }}
                   className="w-full px-4 py-3 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors border border-orange-200 flex items-center justify-between"
                 >
                   <div className="flex items-center">
                     <i className="fas fa-clock mr-2"></i>
                     <span className="font-medium">Pesées en attente</span>
                   </div>
                   <div className="flex items-center">
                     <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded-full text-sm font-semibold">
                       {pendingWeighingsCount}
                     </span>
                     <i className="fas fa-chevron-right ml-2"></i>
                   </div>
                 </button>
               </div>
             </PermissionGuard>

             {/* Sections Origin et Destination - Sans cartes */}
             <div className="space-y-4">
               <OriginSection 
                 formData={{
                   provenance: formData.provenance,
                   connaissement: formData.connaissement
                 }}
                 onInputChange={handleInputChange}
               />
               
               <DestinationSection 
                 formData={{
                   destination: destinationFormData.destination,
                   magasin: destinationFormData.magasin,
                   numeroLot: destinationFormData.numeroLot
                 }}
                 onInputChange={handleDestinationInputChange}
               />
             </div>

           </div>
        </div>
          </>
        )}
      </div>


      {/* Modal des détails complets de la pesée */}
      {showWeighingDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Détails complets de la pesée</h2>
                <button
                  onClick={() => setShowWeighingDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              {currentWeighing ? (
                <div className="space-y-6">
                  {/* Informations générales */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Informations générales</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Référence:</span>
                        <p className="font-medium">{getDisplayPeseeCode(currentWeighing)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Date/Heure:</span>
                        <p className="font-medium">
                          {currentWeighing.date_heure ? new Date(currentWeighing.date_heure).toLocaleString('fr-FR') : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Statut:</span>
                        <p className="font-medium">{currentWeighing.statut_display}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Type de pesée:</span>
                        <p className="font-medium">{currentWeighing.type_pesee_display}</p>
                      </div>
                    </div>
                  </div>

                  {/* Véhicule et produit */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Véhicule et Produit</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Véhicule:</span>
                        <p className="font-medium">{currentWeighing.camion_immatriculation}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Produit:</span>
                        <p className="font-medium">{currentWeighing.produit_nom}</p>
                      </div>
                    </div>
                  </div>

                  {/* Poids */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Poids</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Poids Brut:</span>
                        <p className="font-medium text-lg">{currentWeighing.poids_brut ? `${currentWeighing.poids_brut} kg` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Poids Tare:</span>
                        <p className="font-medium text-lg">{currentWeighing.poids_tare ? `${currentWeighing.poids_tare} kg` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Poids Net:</span>
                        <p className="font-medium text-lg text-green-600">{currentWeighing.poids_net ? `${currentWeighing.poids_net} kg` : 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Partenaires */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Partenaires</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Chauffeur:</span>
                        <p className="font-medium">{currentWeighing.chauffeur_nom || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Fournisseur:</span>
                        <p className="font-medium">{currentWeighing.fournisseur_nom || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Client:</span>
                        <p className="font-medium">{currentWeighing.client_nom || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Détails supplémentaires */}
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Détails supplémentaires</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Provenance:</span>
                        <p className="font-medium">{currentWeighing.provenance || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Destination:</span>
                        <p className="font-medium">{currentWeighing.destination || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">N° Lot:</span>
                        <p className="font-medium">{currentWeighing.numero_lot || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Observations:</span>
                        <p className="font-medium">{currentWeighing.observations || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Analyse qualité si disponible */}
                  {qualityAnalysis && (
                    <div className="bg-orange-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Analyse Qualité</h3>
                      
                      {/* Paramètres de qualité */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm text-gray-600">Décision:</span>
                          <p className="font-medium">
                            {qualityAnalysis.quality_decision === 'ACCEPTE' && 'Accepté'}
                            {qualityAnalysis.quality_decision === 'ACCEPTE_RESERVE' && 'Accepté avec réserve'}
                            {qualityAnalysis.quality_decision === 'REFUSE' && 'Refusé'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Nombres de qualité:</span>
                          <p className="font-medium">{qualityAnalysis.quality_numbers || 'N/A'}</p>
                        </div>
                        
            
                        <div>
                          <span className="text-sm text-gray-600">Grainnage:</span>
                          <p className="font-medium">{qualityAnalysis.broken_grains}%</p>
                        </div>
                        
                      </div>

                      {/* Comptage des sacs */}
                      <div className="border-t border-orange-200 pt-4">
                        <h4 className="font-medium text-gray-700 mb-3">Comptage des sacs</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div className="text-center">
                            <div className="text-gray-600">Déclarés</div>
                            <div className="font-semibold text-lg">{formData.nbSacsDeclares || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-green-600">Sains</div>
                            <div className="font-semibold text-lg text-green-600">{formData.nbSacsSains || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-blue-600">Reconditionnés</div>
                            <div className="font-semibold text-lg text-blue-600">{formData.nbSacsRecond || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-orange-600">Humides</div>
                            <div className="font-semibold text-lg text-orange-600">{formData.nbSacsHumides || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-red-600">Déchirés</div>
                            <div className="font-semibold text-lg text-red-600">{formData.nbSacsDechires || 0}</div>
                          </div>
                        </div>
                      </div>

                      {/* Observations */}
                      <div className="border-t border-orange-200 pt-4 mt-4">
                        <span className="text-sm text-gray-600">Observations:</span>
                        <p className="font-medium mt-1">{qualityAnalysis.observations || formData.observations || 'Aucune observation'}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Aucune information disponible</p>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowWeighingDetailsModal(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de récapitulatif après enregistrement du poids tare */}
      {showTareSummaryModal && tareSummaryData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Récapitulatif de la pesée</h2>
                  <p className="text-sm text-gray-600 mt-1">Vérifiez les informations avant de finaliser</p>
                </div>
                <button
                  onClick={() => setShowTareSummaryModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Informations principales */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 text-center">Pesée terminée avec succès</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Poids Brut</p>
                      <p className="text-lg font-bold text-green-600">
                        {tareSummaryData.poids_brut ? `${parseFloat(tareSummaryData.poids_brut.toString()).toLocaleString()} kg` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Poids Tare</p>
                      <p className="text-lg font-bold text-blue-600">
                        {tareSummaryData.poids_tare ? `${parseFloat(tareSummaryData.poids_tare.toString()).toLocaleString()} kg` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Poids Net</p>
                      <p className="text-2xl font-bold text-green-700">
                        {tareSummaryData.poids_net ? `${parseFloat(tareSummaryData.poids_net.toString()).toLocaleString()} kg` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Détails de la pesée */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Détails de la pesée</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Référence:</span>
                      <p className="font-medium">{tareSummaryData.reference}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Produit:</span>
                      <p className="font-medium">{tareSummaryData.produit || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Véhicule:</span>
                      <p className="font-medium">{tareSummaryData.vehicule || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Chauffeur:</span>
                      <p className="font-medium">{tareSummaryData.chauffeur || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Destination:</span>
                      <p className="font-medium">{tareSummaryData.destination || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">N° Lot:</span>
                      <p className="font-medium">{tareSummaryData.numero_lot || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Informations complémentaires */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Informations complémentaires</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Provenance:</span>
                      <p className="font-medium">{tareSummaryData.provenance || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Connaissement:</span>
                      <p className="font-medium">{tareSummaryData.connaissement || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">N° Transfert:</span>
                      <p className="font-medium">{tareSummaryData.numero_transfert || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sticker:</span>
                      <p className="font-medium">{tareSummaryData.sticker || 'N/A'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-sm text-gray-600">Observations:</span>
                      <p className="font-medium">{tareSummaryData.observations || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Analyse qualité */}
                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Analyse qualité</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Sacs déclarés:</span>
                      <p className="font-medium text-lg">{tareSummaryData.nb_sacs_declares || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs sains:</span>
                      <p className="font-medium text-lg text-green-600">{tareSummaryData.nb_sacs_sains || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs reconditionnés:</span>
                      <p className="font-medium text-lg text-blue-600">{tareSummaryData.nb_sacs_recond || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs humides:</span>
                      <p className="font-medium text-lg text-yellow-600">{tareSummaryData.nb_sacs_humides || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs déchirés:</span>
                      <p className="font-medium text-lg text-red-600">{tareSummaryData.nb_sacs_dechires || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Total analysé:</span>
                      <p className="font-medium text-lg text-purple-600">
                        {(tareSummaryData.nb_sacs_sains || 0) + 
                         (tareSummaryData.nb_sacs_recond || 0) + 
                         (tareSummaryData.nb_sacs_humides || 0) + 
                         (tareSummaryData.nb_sacs_dechires || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Partenaires */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Partenaires</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Fournisseur:</span>
                      <p className="font-medium">{tareSummaryData.fournisseur || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Client:</span>
                      <p className="font-medium">{tareSummaryData.client || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-center space-x-4 mt-8">
                <PermissionButton
                  permission={PERMISSIONS.WEIGHING_EDIT}
                  onClick={handleTareSummaryReprendre}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center"
                  title="Annuler l'enregistrement et reprendre l'étape 3"
                  disabledMessage="Permission insuffisante pour modifier une pesée"
                >
                  <i className="fas fa-undo mr-2"></i>
                  Reprendre
                </PermissionButton>
                <PermissionButton
                  permission={PERMISSIONS.WEIGHING_COMPLETE}
                  onClick={handleTareSummaryTerminer}
                  className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center"
                  title="Finaliser la pesée et passer à l'impression du reçu"
                  disabledMessage="Permission insuffisante pour compléter une pesée"
                >
                  <i className="fas fa-check-circle mr-2"></i>
                  Terminer la pesée
                </PermissionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal des pesées en attente */}
      {showPendingWeighingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-[10000]">
          <div className="bg-white h-full w-full max-w-lg shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-end items-center mb-6">
                <button
                  onClick={() => setShowPendingWeighingsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <PermissionGuard 
                permission={PERMISSIONS.WEIGHING_VIEW}
                fallback={
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">Permission insuffisante pour voir les pesées en attente</p>
                  </div>
                }
              >
                <PendingWeighingsList 
                  onWeighingSelect={(weighing) => {
                    handleSelectPendingWeighing(weighing);
                    setShowPendingWeighingsModal(false);
                  }}
                  loadedWeighingId={loadedWeighingId ?? undefined}
                />
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}

      </div>
    </PermissionGuard>
  );
};

export default NouvellePeseePage;
